import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import Checkbox from "../../../components/BaseUI/Checkbox";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import Pagination from "../../../components/BaseUI/Pagination";
import {
  FiPlus,
  FiX,
  FiArrowLeft,
  FiChevronDown,
  FiChevronRight,
  FiChevronRight as FiChevronRightIcon,
  FiCheck,
  FiSave,
  FiServer,
  FiList,
  FiPackage,
  FiTrash2,
  FiEdit2,
  FiClock,
  FiZap,
  FiEye,
  FiEyeOff,
  FiLink,
  FiMinusCircle,
  FiRotateCcw,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import {
  VARIANTS_PER_PAGE,
  ASSIGNMENT_STEPS,
  partBaseLabel,
  partVariantLabel,
  partInputLabel,
  partUnitLabel,
  partUnitLabelForDoc,
  quantityWithUnit,
  labelWithUnit,
  partDocById,
  mainPartsToSelectOptions,
  ensureSelectedInSelectOptions,
  getOutputSettings,
  defaultOutputSettings,
  settingsFromPartDoc,
  ensureOutputSettingsForVariants,
  collectVariantSettingsIssues,
  isVariantSettingsComplete,
  emptyInputEntry,
  emptyPartAssignment,
  emptyMachineFormMulti,
  recordsToMultiPartAssignments,
  inputMainConfigured,
  resolveInputPartIds,
  resolveEntryAllowedPartIds,
  getEntryConsumptionRatio,
  formatEntryConsumptionSummary,
  syncEntryConsumptionRatios,
  mergeDefaultEntryConsumption,
  entryAllowedVariantsComplete,
  syncInputEntriesFromOutput,
  collectAssignmentValidationIssues,
  inputMaterialsForOutputVariant,
  inputVariantsForMain,
  inputMainPartFromCatalog,
  buildManualAssignmentsByVariantMatch,
  formatAutoAssignUnmatchedMessage,
  computeOutputEquivalentWeight,
  formatEquivalentWeight,
  formatInputEquivalentHint,
  inferEquivalentUnitSymbol,
  resolveOutputDisplayWeight,
  getMetricDisplayParts,
} from "./partMachinesUtils";
import "./PartMachines.scss";

const MetricAmount = ({ value, unit, className, title }) => {
  const { value: shown, unit: u } = getMetricDisplayParts(value, unit);
  const full = u ? `${shown} ${u}` : shown;
  return (
    <span className={className} title={title || full}>
      <span className="assign-metric__value">{shown}</span>
      {u ? <span className="assign-metric__unit">{u}</span> : null}
    </span>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const ENTRY_VARIANTS_PER_PAGE = 10;

const paginateVariantTable = (items, page) => {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / ENTRY_VARIANTS_PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * ENTRY_VARIANTS_PER_PAGE;
  return {
    total,
    totalPages,
    page: safePage,
    items: items.slice(start, start + ENTRY_VARIANTS_PER_PAGE),
  };
};

const variantTableWrapClass = (total, baseClass) =>
  `${baseClass}${total >= ENTRY_VARIANTS_PER_PAGE ? ` ${baseClass}--scroll` : ""}`;

const VariantTablePagination = ({ total, page, onPageChange }) => {
  if (total < ENTRY_VARIANTS_PER_PAGE) return null;
  const totalPages = Math.max(1, Math.ceil(total / ENTRY_VARIANTS_PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  return (
    <div className="assign-variant-table-pagination">
      <Pagination
        totalItems={total}
        totalPerPage={ENTRY_VARIANTS_PER_PAGE}
        currentPage={safePage}
        options={{
          selectQuantity: false,
          simpleCounting: true,
          skipPrevious: true,
          skipNext: true,
          previous: true,
          next: true,
        }}
        onPageChange={({ pagination }) =>
          onPageChange(pagination?.currentPage || 1)
        }
      />
    </div>
  );
};

const VariantTablePanel = ({
  total,
  page,
  onPageChange,
  wrapClass,
  children,
}) => (
  <div className="assign-variant-table-panel">
    <div className={variantTableWrapClass(total, wrapClass)}>{children}</div>
    <VariantTablePagination
      total={total}
      page={page}
      onPageChange={onPageChange}
    />
  </div>
);

const entryInputSummary = (entry, idx, mainPartById, catalog) => {
  const inMain = entry.inputMainPartId
    ? mainPartById(entry.inputMainPartId)
    : null;
  if (!inMain) {
    return {
      title: `Girdi ${idx + 1}`,
      meta: "girdi tanımlayın",
    };
  }
  const mp = mainPartById(entry.inputMainPartId);
  const mainVariantCount = Number(mp?.variantCount) || 0;
  const allowedCount = resolveEntryAllowedPartIds(
    entry,
    catalog,
    mainPartById,
  ).length;
  const countLabel =
    mainVariantCount > 0 ? `${allowedCount} varyant` : "1 parça";
  const ratioMeta = formatEntryConsumptionSummary(entry, catalog, mainPartById);
  const equivHint = formatInputEquivalentHint(inMain);
  const metaParts = [countLabel, ratioMeta, equivHint].filter(Boolean);
  return {
    title: partBaseLabel(inMain),
    meta: metaParts.join(" · "),
  };
};

const outputComplete = (output) =>
  !!output?.baseKey && (output.selectedPartIds || []).length > 0;

const PartMachineAssignmentForm = () => {
  const { machineId: routeMachineId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!routeMachineId;

  useTitle(
    isEdit
      ? "Rovion MES - Makine Ataması Düzenle"
      : "Rovion MES - Yeni Makine Ataması",
  );

  const { getToken, user, loading: authLoading } = useAuth();
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/part-machines",
      user.pages || [],
      !!user.fullAccess,
    );

  const [mainParts, setMainParts] = useState([]);
  const [inputParts, setInputParts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [models, setModels] = useState([]);
  const [assignedMachineIds, setAssignedMachineIds] = useState(new Set());
  const [loadingInit, setLoadingInit] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingMachineId, setEditingMachineId] = useState(
    routeMachineId || null,
  );
  const [formData, setFormData] = useState(emptyMachineFormMulti());
  const [mergeConfirmModal, setMergeConfirmModal] = useState({
    isOpen: false,
    machineId: null,
    machineLabel: "",
    recordCount: 0,
  });
  const [saveMergeMode, setSaveMergeMode] = useState(false);

  // Multi-part tab state
  const [activePartIdx, setActivePartIdx] = useState(0);
  const activePartIdxRef = useRef(0);
  useEffect(() => {
    activePartIdxRef.current = activePartIdx;
  });

  const [activeEntryIdx, setActiveEntryIdx] = useState(0);
  const [outputMainSearchQuery, setOutputMainSearchQuery] = useState("");
  const [outputVariantsLoading, setOutputVariantsLoading] = useState(false);
  const [outputMainSelectOptions, setOutputMainSelectOptions] = useState([]);
  const [inputMainSelectOptions, setInputMainSelectOptions] = useState([]);
  const outputMainSearchDebounce = useRef(null);
  const inputMainSearchDebounce = useRef(null);
  const [activeStep, setActiveStep] = useState(1);
  const [variantSectionOpen, setVariantSectionOpen] = useState({});
  const [maxReachedStep, setMaxReachedStep] = useState(1);
  const [entryEditModal, setEntryEditModal] = useState({
    open: false,
    idx: null,
  });
  const [entryVariantPage, setEntryVariantPage] = useState(1);
  const [summaryVariantPageByPart, setSummaryVariantPageByPart] = useState({});
  const [entryBulkRatio, setEntryBulkRatio] = useState("1");
  const [entryBulkPickKeys, setEntryBulkPickKeys] = useState(() => new Set());
  const [summaryOpen, setSummaryOpen] = useState({});
  const [wastageModal, setWastageModal] = useState({
    open: false,
    outputPartId: null,
    draft: "0",
    unitSymbol: "",
  });
  const [wastageBulkModal, setWastageBulkModal] = useState({
    open: false,
    draft: "0",
    targetIds: [],
    unitSymbol: "",
  });

  // Drag-drop assignment state (step 3)
  const [showAssigned, setShowAssigned] = useState(false);
  const [selectedInputKeys, setSelectedInputKeys] = useState(new Set());
  const [dragOverOutputId, setDragOverOutputId] = useState(null);
  const [bulkAssignModal, setBulkAssignModal] = useState({
    open: false,
    targetIds: [],
  });
  const [dndInputQuery, setDndInputQuery] = useState("");
  const [dndOutputQuery, setDndOutputQuery] = useState("");
  const [dndInputPage, setDndInputPage] = useState(1);
  const [dndOutputPage, setDndOutputPage] = useState(1);

  const outputPanelBodyRef = useRef(null);
  const inputPanelBodyRef = useRef(null);
  const dndDragActiveRef = useRef(false);
  const dndScrollRafRef = useRef(null);
  const dndPointerYRef = useRef(0);
  const dndPageAdvanceAtRef = useRef(0);
  const dndMetaRef = useRef({
    outputPage: 1,
    outputTotalPages: 1,
    inputPage: 1,
    inputTotalPages: 1,
  });

  const DND_SCROLL_EDGE = 48;
  const DND_SCROLL_STEP = 4;
  const DND_PAGE_HOLD_MS = 550;

  const isSummaryOpen = (key) => summaryOpen[key] === true;
  const toggleSummaryOpen = (key) =>
    setSummaryOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  // Reset per-step UI when switching parts
  useEffect(() => {
    setActiveEntryIdx(0);
    setVariantSectionOpen({});
    setEntryEditModal({ open: false, idx: null });
    setShowAssigned(false);
    setSelectedInputKeys(new Set());
    setDragOverOutputId(null);
    setBulkAssignModal({ open: false, targetIds: [] });
    setDndInputQuery("");
    setDndOutputQuery("");
    setDndInputPage(1);
    setDndOutputPage(1);
  }, [activePartIdx]);

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const partsCatalog = useMemo(() => {
    const byId = new Map();
    for (const p of [...mainParts, ...inputParts]) {
      const id = String(p?._id || "");
      if (id) byId.set(id, p);
    }
    return [...byId.values()];
  }, [mainParts, inputParts]);

  const mainPartById = (id) => {
    const idStr = String(id || "");
    const fromOutput = mainParts.find((p) => String(p._id) === idStr);
    if (fromOutput) return fromOutput;
    return inputMainPartFromCatalog(idStr, partsCatalog);
  };

  // Active part shortcuts
  const activePart =
    formData.parts[activePartIdx] ?? formData.parts[0] ?? emptyPartAssignment();
  const activeInputs = activePart.inputs;
  const activeOutput = activePart.output;

  const validationIssues = useMemo(
    () =>
      collectAssignmentValidationIssues(
        activeOutput,
        activeInputs,
        partsCatalog,
        mainPartById,
      ),
    [activeOutput, activeInputs, partsCatalog, mainParts],
  );

  const reportValidationIssues = (issues) => {
    if (!issues.length) return true;
    const first = issues[0];
    addToast(first.message, "danger");
    if (first.entryIdx != null) setActiveEntryIdx(first.entryIdx);
    if (issues.length > 1) {
      addToast(`+${issues.length - 1} ek sorun (aşağıdaki listede)`, "warning");
    }
    return false;
  };

  const machineOptions = machines.map((m) => ({
    value: String(m._id),
    label: `${m.code} — ${m.name}`,
  }));

  const modelOptions = [
    { value: "", label: "— Bağlı değil —" },
    ...models.map((m) => ({
      value: String(m._id),
      label: `${m.code} — ${m.name}`,
    })),
  ];

  const fetchVariantsForMain = useCallback(
    async (mainPartId, page = 1, search = "") => {
      const params = { page, limit: VARIANTS_PER_PAGE };
      const q = String(search || "").trim();
      if (q) params.search = q;
      const { data } = await axios.get(
        `${API_URL}/api/part-machines/main-parts/${mainPartId}/variants`,
        { ...authHeaders(), params },
      );
      return {
        items: data.variants || [],
        page: data.page || page,
        total: data.total ?? 0,
        totalPages: data.totalPages || 1,
      };
    },
    [authHeaders],
  );

  const loadOutputMainSelectOptions = useCallback(
    async (search = "", selectedId = "") => {
      try {
        const params = {};
        if (String(search).trim()) params.search = String(search).trim();
        const { data } = await axios.get(
          `${API_URL}/api/part-machines/main-parts`,
          { ...authHeaders(), params },
        );
        let options = mainPartsToSelectOptions(data.mainParts || []);
        options = ensureSelectedInSelectOptions(options, selectedId, mainParts);
        setOutputMainSelectOptions(options);
      } catch {
        addToast("Çıktı parçaları yüklenemedi", "danger");
      }
    },
    [authHeaders, mainParts, addToast],
  );

  const loadInputMainSelectOptions = useCallback(
    async (search = "", excludeId = "", selectedId = "") => {
      try {
        const params = {};
        if (String(search).trim()) params.search = String(search).trim();
        if (excludeId) params.excludeId = String(excludeId);
        const { data } = await axios.get(
          `${API_URL}/api/part-machines/input-main-parts`,
          { ...authHeaders(), params },
        );
        let options = mainPartsToSelectOptions(data.mainParts || []);
        options = ensureSelectedInSelectOptions(
          options,
          selectedId,
          partsCatalog,
        );
        setInputMainSelectOptions(options);
      } catch {
        addToast("Girdi parçaları yüklenemedi", "danger");
      }
    },
    [authHeaders, partsCatalog, addToast],
  );

  const handleOutputMainSearch = (query) => {
    setOutputMainSearchQuery(query);
    clearTimeout(outputMainSearchDebounce.current);
    outputMainSearchDebounce.current = setTimeout(() => {
      loadOutputMainSelectOptions(query, activeOutput?.baseKey || "");
    }, 300);
  };

  const handleInputMainSearch = (query) => {
    clearTimeout(inputMainSearchDebounce.current);
    inputMainSearchDebounce.current = setTimeout(() => {
      const entry = activeInputs[entryEditModal.idx ?? 0];
      loadInputMainSelectOptions(
        query,
        activeOutput?.baseKey || "",
        entry?.inputMainPartId || "",
      );
    }, 300);
  };

  const fetchAllVariantsForMain = useCallback(
    async (mainPartId, search = "") => {
      const first = await fetchVariantsForMain(mainPartId, 1, search);
      let items = [...first.items];
      for (let p = 2; p <= first.totalPages; p++) {
        const res = await fetchVariantsForMain(mainPartId, p, search);
        items = items.concat(res.items);
      }
      return items;
    },
    [fetchVariantsForMain],
  );

  // ----- Active-part update helpers -----

  const updateActivePart = useCallback((patch) => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      parts[idx] = { ...parts[idx], ...patch };
      return { ...s, parts };
    });
  }, []);

  const updateInputEntry = (i, patch) =>
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const inputs = [...parts[idx].inputs];
      inputs[i] = { ...inputs[i], ...patch };
      parts[idx] = { ...parts[idx], inputs };
      return { ...s, parts };
    });

  const updateOutput = (patch) =>
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      parts[idx] = {
        ...parts[idx],
        output: { ...parts[idx].output, ...patch },
      };
      return { ...s, parts };
    });

  const applyOutputSelection = (outputPatch, selectedPartIds) => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const part = parts[idx];
      const output = { ...part.output, ...outputPatch };
      const ids =
        selectedPartIds !== undefined
          ? selectedPartIds
          : output.selectedPartIds || [];
      // Remove manual assignments for deselected output variants
      const existingAssignments = output.manualInputAssignments || {};
      const idsSet = new Set(ids.map(String));
      const cleanedAssignments = Object.fromEntries(
        Object.entries(existingAssignments).filter(([k]) => idsSet.has(k)),
      );
      const recordIdsByPartId = Object.fromEntries(
        Object.entries(output.recordIdsByPartId || {}).filter(([k]) =>
          idsSet.has(k),
        ),
      );
      const excluded = (output.excludedPartIds || [])
        .map(String)
        .filter((id) => !idsSet.has(id));
      parts[idx] = {
        ...part,
        output: {
          ...output,
          selectedPartIds: ids,
          excludedPartIds:
            outputPatch.excludedPartIds !== undefined
              ? (outputPatch.excludedPartIds || []).map(String)
              : excluded,
          recordIdsByPartId:
            outputPatch.recordIdsByPartId !== undefined
              ? outputPatch.recordIdsByPartId
              : recordIdsByPartId,
          manualInputAssignments: cleanedAssignments,
        },
        inputs: syncInputEntriesFromOutput(part.inputs, ids, partsCatalog),
      };
      return { ...s, parts };
    });
  };

  const excludeOutputVariant = (partId) => {
    const id = String(partId || "");
    if (!id) return;
    const curOut = formData.parts[activePartIdxRef.current]?.output;
    const nextSelected = (curOut?.selectedPartIds || [])
      .map(String)
      .filter((x) => x !== id);
    if (!nextSelected.length) {
      addToast("En az bir çıktı varyantı aktif kalmalı", "warning");
      return;
    }
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx].output;
      const selected = nextSelected;
      const excluded = [
        ...new Set([...(output.excludedPartIds || []).map(String), id]),
      ];
      const assignments = { ...(output.manualInputAssignments || {}) };
      delete assignments[id];
      const recordIdsByPartId = { ...(output.recordIdsByPartId || {}) };
      delete recordIdsByPartId[id];
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          selectedPartIds: selected,
          excludedPartIds: excluded,
          recordIdsByPartId,
          manualInputAssignments: assignments,
        },
        inputs: syncInputEntriesFromOutput(
          parts[idx].inputs,
          selected,
          partsCatalog,
        ),
      };
      return { ...s, parts };
    });
  };

  const restoreOutputVariant = (partId) => {
    const id = String(partId || "");
    if (!id) return;
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx].output;
      const excluded = (output.excludedPartIds || [])
        .map(String)
        .filter((x) => x !== id);
      const selected = [
        ...new Set([...(output.selectedPartIds || []).map(String), id]),
      ];
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          selectedPartIds: selected,
          excludedPartIds: excluded,
        },
        inputs: syncInputEntriesFromOutput(
          parts[idx].inputs,
          selected,
          partsCatalog,
        ),
      };
      return { ...s, parts };
    });
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (!canWrite) {
      navigate("/part-machines", { replace: true });
      return;
    }
    const load = async () => {
      setLoadingInit(true);
      try {
        const [recordsRes, mainRes, inputRes, machinesRes, modelsRes] =
          await Promise.all([
            axios.get(`${API_URL}/api/part-machines`, authHeaders()),
            axios.get(`${API_URL}/api/part-machines/main-parts`, authHeaders()),
            axios.get(
              `${API_URL}/api/part-machines/parts-for-input`,
              authHeaders(),
            ),
            axios.get(`${API_URL}/api/machines`, authHeaders()),
            axios.get(`${API_URL}/api/device-models`, authHeaders()),
          ]);
        const records = recordsRes.data.partMachines || [];
        const mids = new Set();
        records.forEach((r) => {
          const mid = String(r.machine?._id || r.machine || "");
          if (mid) mids.add(mid);
        });
        setAssignedMachineIds(mids);
        const loadedMainParts = mainRes.data.mainParts || [];
        setMainParts(loadedMainParts);
        setOutputMainSelectOptions(mainPartsToSelectOptions(loadedMainParts));
        setInputParts(inputRes.data.parts || []);
        setMachines(machinesRes.data.machines || machinesRes.data || []);
        const md = modelsRes.data;
        setModels(Array.isArray(md) ? md : md.models || []);

        const catalog = [
          ...(mainRes.data.mainParts || []),
          ...(inputRes.data.parts || []),
        ];

        if (routeMachineId) {
          const { data } = await axios.get(
            `${API_URL}/api/part-machines/by-machine/${routeMachineId}`,
            authHeaders(),
          );
          const parts = recordsToMultiPartAssignments(
            data.partMachines || [],
            catalog,
          );
          setFormData({ machine: routeMachineId, parts });
          setEditingMachineId(routeMachineId);
          setMaxReachedStep(ASSIGNMENT_STEPS.length);
        }
      } catch {
        addToast("Veriler yüklenemedi", "danger");
      } finally {
        setLoadingInit(false);
      }
    };
    load();
  }, [
    routeMachineId,
    authLoading,
    user,
    canWrite,
    authHeaders,
    addToast,
    navigate,
  ]);

  useEffect(() => {
    if (activeStep !== 3) return;
    loadOutputMainSelectOptions("", activeOutput?.baseKey || "");
  }, [activeStep, activeOutput?.baseKey, loadOutputMainSelectOptions]);

  useEffect(() => {
    if (!entryEditModal.open || entryEditModal.idx == null) return;
    const entry = activeInputs[entryEditModal.idx];
    loadInputMainSelectOptions(
      "",
      activeOutput?.baseKey || "",
      entry?.inputMainPartId || "",
    );
  }, [
    entryEditModal.open,
    entryEditModal.idx,
    activeOutput?.baseKey,
    loadInputMainSelectOptions,
    activeInputs,
  ]);

  // Ensure default variant selection = "all variants" for entries that have variants
  // (e.g. records loaded from DB that don't yet have allowedPartIds)
  useEffect(() => {
    if (!entryEditModal.open || entryEditModal.idx == null) return;
    const idx = entryEditModal.idx;
    const entry = activeInputs[idx];
    if (!entry?.inputMainPartId) return;
    const mp = mainPartById(entry.inputMainPartId);
    const variantCount = Number(mp?.variantCount) || 0;
    if (variantCount <= 0) return;

    const explicit = (entry.allowedPartIds || []).map(String).filter(Boolean);
    if (explicit.length > 0) return;

    const effective = resolveEntryAllowedPartIds(
      entry,
      partsCatalog,
      mainPartById,
    );
    if (!effective.length) return;
    updateEntryAllowedPartIds(idx, effective);
  }, [
    entryEditModal.open,
    entryEditModal.idx,
    activeInputs,
    partsCatalog,
    mainParts,
  ]);

  // ----- Step validation -----

  const validateOutputAssignment = (output, inputs) => {
    const issues = collectAssignmentValidationIssues(
      output,
      inputs,
      partsCatalog,
      mainPartById,
    );
    return reportValidationIssues(issues);
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.machine) {
        addToast("Makine seçiniz", "danger");
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!activeInputs.length) {
        addToast("En az bir girdi ekleyin", "danger");
        return false;
      }
      const missingInput = activeInputs
        .map((entry, i) => ({ entry, i }))
        .filter(({ entry }) => !inputMainConfigured(entry));
      if (missingInput.length) {
        const n = missingInput.map(({ i }) => i + 1).join(", ");
        addToast(`Girdi ${n}: ana parça ve miktar girin`, "danger");
        if (missingInput[0]) setActiveEntryIdx(missingInput[0].i);
        return false;
      }

      for (let i = 0; i < activeInputs.length; i++) {
        const entry = activeInputs[i];
        if (
          entry?.inputMainPartId &&
          !entryAllowedVariantsComplete(entry, partsCatalog, mainPartById)
        ) {
          addToast(`Girdi ${i + 1}: en az bir varyant seçin`, "danger");
          setActiveEntryIdx(i);
          return false;
        }
      }
      return true;
    }
    if (step === 3) {
      return validateOutputAssignment(activeOutput, activeInputs);
    }
    if (step === 4) {
      const assignmentIssues = collectAssignmentValidationIssues(
        activeOutput,
        activeInputs,
        partsCatalog,
        mainPartById,
      );
      if (assignmentIssues.length) {
        return reportValidationIssues(assignmentIssues);
      }
      const variantIssues = collectVariantSettingsIssues(
        activeOutput,
        partsCatalog,
      );
      if (variantIssues.length) {
        addToast(variantIssues[0].message, "danger");
        if (variantIssues.length > 1) {
          addToast(
            `+${variantIssues.length - 1} ek sorun (listedeki varyantlar)`,
            "warning",
          );
        }
        return false;
      }
      return true;
    }
    return true;
  };

  const goToStep = (step) => {
    if (step < 1 || step > ASSIGNMENT_STEPS.length) return;
    if (step > activeStep) {
      for (let s = activeStep; s < step; s++) {
        if (!validateStep(s)) return;
      }
    }
    setActiveStep(step);
    setMaxReachedStep((m) => Math.max(m, step));
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    const next = Math.min(activeStep + 1, ASSIGNMENT_STEPS.length);
    setActiveStep(next);
    setMaxReachedStep((m) => Math.max(m, next));
  };

  const handlePrev = () => setActiveStep((s) => Math.max(1, s - 1));

  // ----- Machine selection -----

  const handleMachineSelect = async (machineId) => {
    if (!machineId) {
      setFormData((s) => ({ ...s, machine: "" }));
      setSaveMergeMode(false);
      return;
    }
    if (!editingMachineId && assignedMachineIds.has(machineId)) {
      const m = machines.find((x) => String(x._id) === machineId);
      const label = [m?.code, m?.name].filter(Boolean).join(" — ");
      try {
        const { data } = await axios.get(
          `${API_URL}/api/part-machines/by-machine/${machineId}`,
          authHeaders(),
        );
        setMergeConfirmModal({
          isOpen: true,
          machineId,
          machineLabel: label,
          recordCount: (data.partMachines || []).length,
        });
      } catch {
        addToast("Mevcut kayıtlar yüklenemedi", "danger");
      }
      return;
    }
    setFormData((s) => ({ ...s, machine: machineId }));
    setSaveMergeMode(false);
  };

  const confirmMergeExisting = async () => {
    const mid = mergeConfirmModal.machineId;
    if (!mid) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-machines/by-machine/${mid}`,
        authHeaders(),
      );
      const parts = recordsToMultiPartAssignments(
        data.partMachines || [],
        partsCatalog,
      );
      setFormData({ machine: mid, parts });
      setEditingMachineId(mid);
      setActivePartIdx(0);
      setActiveEntryIdx(0);
      setMergeConfirmModal({
        isOpen: false,
        machineId: null,
        machineLabel: "",
        recordCount: 0,
      });
      navigate(`/part-machines/assign/${mid}`, { replace: true });
      setMaxReachedStep(ASSIGNMENT_STEPS.length);
      addToast("Mevcut kayıtlar yüklendi", "success");
    } catch {
      addToast("Mevcut kayıtlar yüklenemedi", "danger");
    }
  };

  // ----- Multi-part tab actions -----

  const addPart = () => {
    const newIdx = formData.parts.length;
    setFormData((s) => ({
      ...s,
      parts: [...s.parts, emptyPartAssignment()],
    }));
    setActivePartIdx(newIdx);
    setActiveStep(2); // go to inputs step for new part
    setMaxReachedStep((m) => Math.max(m, 2));
  };

  const removePart = (idx) => {
    setFormData((s) => {
      if (s.parts.length <= 1) return s;
      const parts = [...s.parts];
      parts.splice(idx, 1);
      return { ...s, parts };
    });
    setActivePartIdx((cur) => {
      if (cur >= idx && cur > 0) return cur - 1;
      return cur;
    });
  };

  // ----- Input entry actions -----

  const addInputEntry = () => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const next = [...parts[idx].inputs, emptyInputEntry()];
      setActiveEntryIdx(next.length - 1);
      parts[idx] = { ...parts[idx], inputs: next };
      return { ...s, parts };
    });
  };

  const removeInputEntry = (i) => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const next = [...parts[idx].inputs];
      next.splice(i, 1);
      const inputs = next.length ? next : [emptyInputEntry()];
      setActiveEntryIdx((cur) => {
        if (cur >= inputs.length) return Math.max(0, inputs.length - 1);
        if (cur > i) return cur - 1;
        return cur;
      });
      parts[idx] = { ...parts[idx], inputs };
      return { ...s, parts };
    });
  };

  const updateVariantSettings = (partId, patch) => {
    const id = String(partId);
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx]?.output;
      if (!output) return s;
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          outputSettingsByPartId: {
            ...(output.outputSettingsByPartId || {}),
            [id]: { ...getOutputSettings(output, id), ...patch },
          },
        },
      };
      return { ...s, parts };
    });
  };

  const applyBulkWastageToOutputs = useCallback(
    (outputPartIds, wastageValue) => {
      const ids = (outputPartIds || []).map(String).filter(Boolean);
      if (!ids.length) return;
      const value = String(wastageValue ?? "0");
      setFormData((s) => {
        const pIdx = activePartIdxRef.current;
        const parts = [...s.parts];
        const out = parts[pIdx].output;
        const nextSettings = { ...(out.outputSettingsByPartId || {}) };
        for (const id of ids) {
          nextSettings[id] = {
            ...getOutputSettings(out, id),
            wastage: value,
          };
        }
        parts[pIdx] = {
          ...parts[pIdx],
          output: { ...out, outputSettingsByPartId: nextSettings },
        };
        return { ...s, parts };
      });
    },
    [],
  );

  useEffect(() => {
    if (activeStep !== 3 && activeStep !== 4) return;
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const ensured = ensureOutputSettingsForVariants(parts[idx].output);
      const prev = parts[idx].output.outputSettingsByPartId || {};
      if (JSON.stringify(ensured) === JSON.stringify(prev)) return s;
      parts[idx] = {
        ...parts[idx],
        output: { ...parts[idx].output, outputSettingsByPartId: ensured },
      };
      return { ...s, parts };
    });
  }, [activeStep, activeOutput.selectedPartIds, activePartIdx]);

  // ----- Save -----

  const handleSave = async () => {
    // Validate all parts
    if (!formData.machine) {
      addToast("Makine seçiniz", "danger");
      setActiveStep(1);
      return;
    }
    const mpById = (id) =>
      mainParts.find((p) => String(p._id) === String(id)) || null;
    for (let i = 0; i < formData.parts.length; i++) {
      const part = formData.parts[i];
      const label = formData.parts.length > 1 ? `Parça ${i + 1}: ` : "";

      if (!part.inputs.length || !part.inputs.some(inputMainConfigured)) {
        addToast(`${label}en az bir girdi ekleyin`, "danger");
        setActivePartIdx(i);
        setActiveStep(2);
        return;
      }
      const assignmentIssues = collectAssignmentValidationIssues(
        part.output,
        part.inputs,
        partsCatalog,
        mpById,
      );
      if (assignmentIssues.length) {
        addToast(`${label}${assignmentIssues[0].message}`, "danger");
        setActivePartIdx(i);
        setActiveStep(3);
        return;
      }
      const variantIssues = collectVariantSettingsIssues(
        part.output,
        partsCatalog,
      );
      if (variantIssues.length) {
        addToast(`${label}${variantIssues[0].message}`, "danger");
        setActivePartIdx(i);
        setActiveStep(4);
        return;
      }
    }

    setSaving(true);
    try {
      const allParts = formData.parts.flatMap((assignment) => {
        const out = assignment.output;
        const manualAssignments = out.manualInputAssignments || {};
        return (out.selectedPartIds || []).map((partId) => {
          const settings = getOutputSettings(out, partId);
          const inputMaterials = inputMaterialsForOutputVariant(
            partId,
            manualAssignments,
            assignment.inputs,
            partsCatalog,
            mpById,
          ).map((m) => ({
            partId: String(m.partId),
            consumptionRatio: m.consumptionRatio,
          }));
          const measurementAxes = (settings.measurementAxes || []).map((a) => ({
            payloadKey: String(a.payloadKey ?? "").trim(),
            expected: Number(a.expected) || 0,
            tolerance: Number(a.tolerance) || 0,
            operator: a.operator || "eq",
          }));
          return {
            _id: out.recordIdsByPartId?.[partId] || undefined,
            part: partId,
            inputMaterials,
            isActive: out.isActive,
            cycleQuantity: Math.max(0, Number(settings.cycleQuantity) || 1),
            netCycleTime: Number(settings.netCycleTime) || 0,
            wastage: Math.max(0, Number(settings.wastage) || 0),
            productTypeVariable: null,
            measurementAxes,
            isFinalProduct: !!settings.isFinalProduct,
            deviceModel: settings.isFinalProduct
              ? settings.deviceModel || null
              : null,
          };
        });
      });
      await axios.put(
        `${API_URL}/api/part-machines/by-machine/${formData.machine}`,
        { merge: saveMergeMode, parts: allParts },
        authHeaders(),
      );
      addToast(editingMachineId ? "Güncellendi" : "Kaydedildi", "success");
      navigate("/part-machines");
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  // ----- Entry / output helpers -----

  const applyEntryInput = (idx, inputMainPartId, consumptionRatio) => {
    const v = inputMainPartId || "";
    const mp = mainPartById(v);
    const isMainOnly = mp && Number(mp.variantCount) === 0;
    const variants = !isMainOnly ? inputVariantsForMain(v, partsCatalog) : [];
    const defaultAllowed = isMainOnly
      ? [v]
      : variants.map((x) => String(x._id)).filter(Boolean);
    const ratioStr = String(consumptionRatio ?? "1");
    setFormData((s) => {
      const pIdx = activePartIdxRef.current;
      const parts = [...s.parts];
      const inputs = [...parts[pIdx].inputs];
      inputs[idx] = {
        ...inputs[idx],
        inputMainPartId: v,
        selectedPartIds: isMainOnly ? [v] : [],
        partId: isMainOnly ? v : "",
        allowedPartIds: defaultAllowed,
        consumptionRatio: ratioStr,
        consumptionRatioByPartId: syncEntryConsumptionRatios(
          { consumptionRatio: ratioStr },
          defaultAllowed,
          ratioStr,
        ),
        variantPage: 1,
        variantItems: [],
        variantTotal: 0,
        variantTotalPages: 1,
        variantLoading: false,
      };
      const outputIds = parts[pIdx].output.selectedPartIds || [];
      parts[pIdx] = {
        ...parts[pIdx],
        inputs: outputIds.length
          ? syncInputEntriesFromOutput(inputs, outputIds, partsCatalog)
          : inputs,
      };
      return { ...s, parts };
    });
  };

  const updateEntryAllowedPartIds = (idx, nextAllowed) => {
    setFormData((s) => {
      const pIdx = activePartIdxRef.current;
      const parts = [...s.parts];
      const inputs = [...parts[pIdx].inputs];
      if (!inputs[idx]) return s;
      const allowed = Array.isArray(nextAllowed)
        ? nextAllowed.map(String).filter(Boolean)
        : [];
      const entry = inputs[idx];
      inputs[idx] = {
        ...entry,
        allowedPartIds: allowed,
        consumptionRatioByPartId: syncEntryConsumptionRatios(
          entry,
          allowed,
          entry.consumptionRatio,
        ),
      };
      parts[pIdx] = { ...parts[pIdx], inputs };
      return { ...s, parts };
    });
  };

  const updateEntryConsumptionForPart = (idx, partId, ratio) => {
    const pid = String(partId || "");
    if (!pid) return;
    const ratioStr = String(ratio ?? "1");
    setFormData((s) => {
      const pIdx = activePartIdxRef.current;
      const parts = [...s.parts];
      const inputs = [...parts[pIdx].inputs];
      if (!inputs[idx]) return s;
      const entry = inputs[idx];
      const isMainPart = String(entry.inputMainPartId || "") === pid;
      inputs[idx] = {
        ...entry,
        ...(isMainPart ? { consumptionRatio: ratioStr } : {}),
        consumptionRatioByPartId: {
          ...(entry.consumptionRatioByPartId || {}),
          [pid]: ratioStr,
        },
      };
      parts[pIdx] = { ...parts[pIdx], inputs };
      return { ...s, parts };
    });
  };

  const toggleEntryBulkPick = (partId, allowed) => {
    const id = String(partId || "");
    if (!id || !allowed) return;
    setEntryBulkPickKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setEntryBulkPickIds = (partIds, select) => {
    const ids = (partIds || []).map(String).filter(Boolean);
    setEntryBulkPickKeys((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const applyEntryDefaultConsumption = (idx, ratio) => {
    setFormData((s) => {
      const pIdx = activePartIdxRef.current;
      const parts = [...s.parts];
      const inputs = [...parts[pIdx].inputs];
      if (!inputs[idx]) return s;
      const entry = inputs[idx];
      inputs[idx] = {
        ...entry,
        ...mergeDefaultEntryConsumption(
          entry,
          ratio,
          partsCatalog,
          mainPartById,
        ),
      };
      parts[pIdx] = { ...parts[pIdx], inputs };
      return { ...s, parts };
    });
  };

  const closeEntryEdit = () => {
    setEntryEditModal({ open: false, idx: null });
    setEntryVariantPage(1);
    setEntryBulkPickKeys(new Set());
  };

  const applyEntryConsumptionToParts = (idx, partIds, ratio) => {
    const ids = (partIds || []).map(String).filter(Boolean);
    if (!ids.length) return;
    const ratioStr = String(ratio ?? "1");
    setFormData((s) => {
      const pIdx = activePartIdxRef.current;
      const parts = [...s.parts];
      const inputs = [...parts[pIdx].inputs];
      if (!inputs[idx]) return s;
      const entry = inputs[idx];
      const nextByPart = { ...(entry.consumptionRatioByPartId || {}) };
      for (const id of ids) nextByPart[id] = ratioStr;
      inputs[idx] = {
        ...entry,
        consumptionRatioByPartId: nextByPart,
      };
      parts[pIdx] = { ...parts[pIdx], inputs };
      return { ...s, parts };
    });
  };

  const applyAllOutputVariants = async (mainPartId) => {
    const v = String(mainPartId || "");
    if (!v) return;
    const mp = mainPartById(v);
    if (!mp || Number(mp.variantCount) === 0) return;

    const capturedIdx = activePartIdxRef.current;
    setOutputVariantsLoading(true);
    setFormData((s) => {
      const parts = [...s.parts];
      parts[capturedIdx] = {
        ...parts[capturedIdx],
        output: { ...parts[capturedIdx].output, variantLoading: true },
      };
      return { ...s, parts };
    });

    try {
      const all = await fetchAllVariantsForMain(v, "");
      if (!all.length) {
        addToast("Çıktı varyantı bulunamadı", "warning");
        setFormData((s) => {
          const parts = [...s.parts];
          const cur = parts[capturedIdx];
          if (!cur || cur.output.baseKey !== v) return s;
          parts[capturedIdx] = {
            ...cur,
            output: { ...cur.output, variantLoading: false },
          };
          return { ...s, parts };
        });
        return;
      }

      const allIds = all.map((item) => String(item._id));
      const nextSettings = {};
      for (const vp of all) {
        const vid = String(vp._id);
        nextSettings[vid] = {
          ...defaultOutputSettings(),
          ...settingsFromPartDoc(vp),
        };
      }

      applyOutputSelection(
        {
          variantItems: all,
          variantTotal: all.length,
          variantTotalPages: 1,
          variantPage: 1,
          variantLoading: false,
          outputSettingsByPartId: nextSettings,
          excludedPartIds: [],
        },
        allIds,
      );
    } catch {
      addToast("Çıktı varyantları yüklenemedi", "danger");
      setFormData((s) => {
        const parts = [...s.parts];
        const cur = parts[capturedIdx];
        if (!cur || cur.output.baseKey !== v) return s;
        parts[capturedIdx] = {
          ...cur,
          output: { ...cur.output, variantLoading: false },
        };
        return { ...s, parts };
      });
    } finally {
      setOutputVariantsLoading(false);
    }
  };

  const applyMainPartToOutput = (mainPartId) => {
    const v = mainPartId || "";
    const mp = mainPartById(v);
    const isMainOnly = mp && Number(mp.variantCount) === 0;
    const nextSelected = isMainOnly ? [v] : [];
    applyOutputSelection(
      {
        baseKey: v,
        recordIdsByPartId: {},
        variantPage: 1,
        variantItems: [],
        variantTotal: 0,
        variantTotalPages: 1,
        outputSettingsByPartId: isMainOnly
          ? { [v]: settingsFromPartDoc(mp) }
          : {},
        manualInputAssignments: {},
        excludedPartIds: [],
        variantLoading: !isMainOnly && !!v,
      },
      nextSelected,
    );
    setSelectedInputKeys(new Set());
    setBulkAssignModal({ open: false, targetIds: [] });
    if (v && !isMainOnly) void applyAllOutputVariants(v);
  };

  useEffect(() => {
    if (activeStep !== 3) return;
    const baseKey = activeOutput?.baseKey;
    if (!baseKey) return;
    const mp = mainParts.find((p) => String(p._id) === String(baseKey));
    if (!mp || Number(mp.variantCount) === 0) return;
    if ((activeOutput.selectedPartIds || []).length > 0) return;
    void applyAllOutputVariants(baseKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, activePartIdx, activeOutput?.baseKey]);

  // ----- Drag-drop assignment helpers -----

  const resolveAllInputVariants = useCallback(() => {
    const items = [];
    for (let i = 0; i < activeInputs.length; i++) {
      const entry = activeInputs[i];
      if (!entry.inputMainPartId) continue;
      const mp = mainPartById(entry.inputMainPartId);
      const isMainOnly = mp && Number(mp.variantCount) === 0;
      const entryLabel = mp ? partBaseLabel(mp) : `Girdi ${i + 1}`;
      const allowed = resolveEntryAllowedPartIds(
        entry,
        partsCatalog,
        mainPartById,
      );
      const allowedSet = new Set(allowed);
      if (isMainOnly) {
        if (!allowedSet.has(String(entry.inputMainPartId))) continue;
        const doc = partDocById(entry.inputMainPartId, [], partsCatalog) || mp;
        items.push({
          key: `${i}::${entry.inputMainPartId}`,
          partId: entry.inputMainPartId,
          entryIdx: i,
          entryLabel,
          fullLabel: doc ? partInputLabel(doc) : entryLabel,
        });
      } else {
        const variants = inputVariantsForMain(
          entry.inputMainPartId,
          partsCatalog,
        );
        for (const v of variants) {
          const pid = String(v._id);
          if (!allowedSet.has(pid)) continue;
          items.push({
            key: `${i}::${pid}`,
            partId: pid,
            entryIdx: i,
            entryLabel,
            fullLabel: partInputLabel(v),
          });
        }
      }
    }
    return items;
  }, [activeInputs, partsCatalog, mainParts]);

  const autoAssignAll = () => {
    const outputIds = (activeOutput.selectedPartIds || [])
      .map(String)
      .filter(Boolean);
    if (!outputIds.length) {
      addToast("Önce çıktı parçası seçin", "warning");
      return;
    }
    const { assignments, unmatched, totalAssigned } =
      buildManualAssignmentsByVariantMatch(
        outputIds,
        activeInputs,
        partsCatalog,
        mainPartById,
        activeOutput.variantItems || [],
      );
    updateOutput({ manualInputAssignments: assignments });

    const unmatchedDetail = formatAutoAssignUnmatchedMessage(unmatched);
    // if (!totalAssigned) {
    //   addToast(
    //     unmatchedDetail
    //       ? `Eşleşen girdi bulunamadı: ${unmatchedDetail}`
    //       : "Eşleşen girdi bulunamadı (girdi veya çıktı seçimini kontrol edin)",
    //     "warning",
    //   );
    //   return;
    // }
    // if (unmatched.length) {
    //   addToast(
    //     `${totalAssigned} eşleşme atandı; eşleşmeyenler: ${unmatchedDetail}`,
    //     "warning",
    //   );
    //   return;
    // }
    addToast("Eşleşen girdi varyantları atandı", "success");
  };

  const getActiveManualAssignments = () =>
    activeOutput.manualInputAssignments || {};

  const isInputKeyAssigned = (key, allAssignments) => {
    const partId = key.split("::")[1];
    return Object.values(allAssignments).some((list) =>
      (list || []).some((a) => a.partId === partId),
    );
  };

  const assignInputsToOutput = (outputVariantId, items) => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx].output;
      const current = output.manualInputAssignments || {};
      let outputAssignments = [...(current[outputVariantId] || [])];
      for (const { partId, entryIdx } of items) {
        // Deduplicate only by partId — same variant can't be added twice,
        // but multiple variants (even from the same entry) are all allowed
        outputAssignments = outputAssignments.filter(
          (a) => a.partId !== partId,
        );
        const entry = parts[idx].inputs[entryIdx];
        const defaultRatio = entry
          ? getEntryConsumptionRatio(entry, partId)
          : "1";
        outputAssignments.push({
          partId,
          entryIdx,
          consumptionRatio: defaultRatio,
        });
      }
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          manualInputAssignments: {
            ...current,
            [outputVariantId]: outputAssignments,
          },
        },
      };
      return { ...s, parts };
    });
  };

  const unassignInputFromOutput = (outputVariantId, partId) => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx].output;
      const current = output.manualInputAssignments || {};
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          manualInputAssignments: {
            ...current,
            [outputVariantId]: (current[outputVariantId] || []).filter(
              (a) => a.partId !== partId,
            ),
          },
        },
      };
      return { ...s, parts };
    });
  };

  const unassignAllFromOutput = (outputVariantId) => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx].output;
      const current = output.manualInputAssignments || {};
      if (!current[outputVariantId]?.length) return s;
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          manualInputAssignments: {
            ...current,
            [outputVariantId]: [],
          },
        },
      };
      return { ...s, parts };
    });
  };

  const updateAssignedRatio = (outputVariantId, entryIdx, partId, ratio) => {
    const outKey = String(outputVariantId);
    const pid = String(partId || "");
    if (!outKey || !pid) return;
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx]?.output;
      if (!output) return s;
      const current = output.manualInputAssignments || {};
      const list = [...(current[outKey] || [])];
      const itemIdx = list.findIndex(
        (a) => String(a.partId) === pid && a.entryIdx === entryIdx,
      );
      if (itemIdx < 0) return s;
      list[itemIdx] = {
        ...list[itemIdx],
        consumptionRatio: String(ratio ?? ""),
      };
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          manualInputAssignments: {
            ...current,
            [outKey]: list,
          },
        },
      };
      return { ...s, parts };
    });
  };

  const trackDndPointer = useCallback((e) => {
    if (typeof e.clientY === "number") dndPointerYRef.current = e.clientY;
    e.preventDefault();
  }, []);

  const stopDndAutoScroll = useCallback(() => {
    dndDragActiveRef.current = false;
    document.removeEventListener("dragover", trackDndPointer);
    if (dndScrollRafRef.current != null) {
      cancelAnimationFrame(dndScrollRafRef.current);
      dndScrollRafRef.current = null;
    }
  }, [trackDndPointer]);

  const scrollDndPanel = useCallback((el, y, kind) => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const meta = dndMetaRef.current;
    const now = Date.now();
    const canAdvancePage = now - dndPageAdvanceAtRef.current > DND_PAGE_HOLD_MS;

    if (y < rect.top + DND_SCROLL_EDGE) {
      if (el.scrollTop <= 0 && canAdvancePage) {
        if (kind === "output" && meta.outputPage > 1) {
          dndPageAdvanceAtRef.current = now;
          setDndOutputPage((p) => Math.max(1, p - 1));
        } else if (kind === "input" && meta.inputPage > 1) {
          dndPageAdvanceAtRef.current = now;
          setDndInputPage((p) => Math.max(1, p - 1));
        }
      } else {
        el.scrollTop = Math.max(0, el.scrollTop - DND_SCROLL_STEP);
      }
      return;
    }

    if (y > rect.bottom - DND_SCROLL_EDGE) {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      if (atBottom && canAdvancePage) {
        if (kind === "output" && meta.outputPage < meta.outputTotalPages) {
          dndPageAdvanceAtRef.current = now;
          setDndOutputPage((p) => Math.min(meta.outputTotalPages, p + 1));
          el.scrollTop = 0;
          return;
        }
        if (kind === "input" && meta.inputPage < meta.inputTotalPages) {
          dndPageAdvanceAtRef.current = now;
          setDndInputPage((p) => Math.min(meta.inputTotalPages, p + 1));
          el.scrollTop = 0;
          return;
        }
      }
      el.scrollTop = Math.min(
        el.scrollHeight - el.clientHeight,
        el.scrollTop + DND_SCROLL_STEP,
      );
    }
  }, []);

  const startDndAutoScroll = useCallback(() => {
    stopDndAutoScroll();
    dndDragActiveRef.current = true;
    dndPointerYRef.current = 0;
    dndPageAdvanceAtRef.current = 0;
    document.addEventListener("dragover", trackDndPointer, { passive: false });

    const tick = () => {
      if (!dndDragActiveRef.current) return;
      const y = dndPointerYRef.current;
      if (y > 0) {
        const vh = window.innerHeight;
        if (y > vh - DND_SCROLL_EDGE) {
          window.scrollBy(0, DND_SCROLL_STEP);
        } else if (y < DND_SCROLL_EDGE + 72) {
          window.scrollBy(0, -DND_SCROLL_STEP);
        }
        scrollDndPanel(outputPanelBodyRef.current, y, "output");
        scrollDndPanel(inputPanelBodyRef.current, y, "input");
      }
      dndScrollRafRef.current = requestAnimationFrame(tick);
    };

    dndScrollRafRef.current = requestAnimationFrame(tick);
  }, [stopDndAutoScroll, trackDndPointer, scrollDndPanel]);

  useEffect(() => () => stopDndAutoScroll(), [stopDndAutoScroll]);

  const handleDndDragEnd = useCallback(() => {
    stopDndAutoScroll();
    setDragOverOutputId(null);
  }, [stopDndAutoScroll]);

  const unassignAllOutputs = () => {
    setFormData((s) => {
      const idx = activePartIdxRef.current;
      const parts = [...s.parts];
      const output = parts[idx].output;
      const current = output.manualInputAssignments || {};
      const hasAny = Object.values(current).some((list) => list?.length > 0);
      if (!hasAny) return s;
      parts[idx] = {
        ...parts[idx],
        output: {
          ...output,
          manualInputAssignments: Object.fromEntries(
            Object.keys(current).map((k) => [k, []]),
          ),
        },
      };
      return { ...s, parts };
    });
    setSelectedInputKeys(new Set());
    setBulkAssignModal({ open: false, targetIds: [] });
  };

  const handleInputDragStart = (e, item, allInputItems) => {
    const toDrag = selectedInputKeys.has(item.key)
      ? [...selectedInputKeys]
          .map((k) => allInputItems.find((x) => x.key === k))
          .filter(Boolean)
      : [item];
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify(
        toDrag.map(({ partId, entryIdx }) => ({ partId, entryIdx })),
      ),
    );
    e.dataTransfer.effectAllowed = "move";
    startDndAutoScroll();
  };

  const handleOutputDragOver = (e, outputVariantId) => {
    trackDndPointer(e);
    e.dataTransfer.dropEffect = "move";
    setDragOverOutputId(outputVariantId);
  };

  const handleOutputDrop = (e, outputVariantId) => {
    e.preventDefault();
    try {
      const items = JSON.parse(e.dataTransfer.getData("application/json"));
      assignInputsToOutput(outputVariantId, items);
    } catch {}
    handleDndDragEnd();
  };

  const handleBulkAssign = () => {
    const targets = bulkAssignModal.targetIds;
    if (!targets.length || !selectedInputKeys.size) return;
    const allItems = resolveAllInputVariants();
    const toDrag = [...selectedInputKeys]
      .map((k) => allItems.find((x) => x.key === k))
      .filter(Boolean)
      .map(({ partId, entryIdx }) => ({ partId, entryIdx }));
    const targetCount = targets.length;
    for (const outId of targets) {
      assignInputsToOutput(outId, toDrag);
    }
    setSelectedInputKeys(new Set());
    setBulkAssignModal({ open: false, targetIds: [] });
    addToast(`${toDrag.length} girdi ${targetCount} çıktıya atandı`, "success");
  };

  const toggleInputKeySelection = (key) => {
    setSelectedInputKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ----- Render helpers (unchanged from original, using activeOutput/activeInputs) -----

  const renderConfiguredInputsSummary = () => {
    const configured = activeInputs.filter((e) => inputMainConfigured(e));
    if (!configured.length) return null;
    return (
      <div className="alert alert-light border small mb-3 py-2">
        <strong>Girdiler:</strong>{" "}
        {configured
          .map((entry, i) => {
            const { title, meta } = entryInputSummary(
              entry,
              i,
              mainPartById,
              partsCatalog,
            );
            return `${title} (${meta.split(" · ")[0]})`;
          })
          .join(" + ")}
      </div>
    );
  };

  const openEntryEdit = (idx) => {
    setActiveEntryIdx(idx);
    const entry = activeInputs[idx];
    setEntryBulkRatio(String(entry?.consumptionRatio ?? "1"));
    setEntryVariantPage(1);
    const allowed = resolveEntryAllowedPartIds(
      entry,
      partsCatalog,
      mainPartById,
    );
    setEntryBulkPickKeys(new Set(allowed));
    setEntryEditModal({ open: true, idx });
  };

  const renderAssignmentValidationPanel = () => {
    const outputIssues = validationIssues.filter(
      (issue) => issue.kind === "output",
    );
    if (!outputIssues.length) return null;
    return (
      <div className="alert-inline alert-inline--danger mb-3" role="alert">
        <strong className="d-block mb-2">Eksik çıktı atamaları</strong>
        <ul className="mb-0 ps-3">
          {outputIssues.map((issue, i) => (
            <li key={i}>{issue.message}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderEntriesStep = () => (
    <div className="assign-section">
      <div className="entry-list">
        {activeInputs.map((entry, i) => {
          const { title, meta } = entryInputSummary(
            entry,
            i,
            mainPartById,
            partsCatalog,
          );
          const inputOk = inputMainConfigured(entry);
          const entryIssue = validationIssues.find(
            (issue) => issue.kind === "entry" && issue.entryIdx === i,
          );
          const isActive = activeEntryIdx === i;
          return (
            <div
              key={i}
              className={`entry-card${isActive ? " entry-card--active" : ""}${entryEditModal.open && entryEditModal.idx === i ? " entry-card--active" : ""}${entryIssue ? " entry-card--error" : ""}`}
              onClick={() => setActiveEntryIdx(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setActiveEntryIdx(i);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="entry-card__main">
                  <div className="entry-card__title d-flex align-items-center gap-2 flex-wrap">
                    <span>Girdi {i + 1}</span>
                  </div>
                  <div className="entry-card__title small mt-1">{title}</div>
                  <div className="entry-card__meta">{meta}</div>
                </div>
                <div
                  className="entry-card__actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconButton
                    icon={<FiEdit2 />}
                    label="Girdi düzenle"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => openEntryEdit(i)}
                  />
                  {activeInputs.length > 1 && (
                    <IconButton
                      icon={<FiTrash2 />}
                      label="Girdiyi sil"
                      appearance="danger-subtle"
                      shape="circle"
                      onClick={() => {
                        if (entryEditModal.idx === i) closeEntryEdit();
                        removeInputEntry(i);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Button
        className="mt-3"
        appearance="primary-subtle"
        onClick={addInputEntry}
        icon={<FiPlus />}
      >
        Girdi ekle
      </Button>
    </div>
  );

  const renderOutputStep = () => {
    const row = activeOutput;
    const mainPart = mainPartById(row.baseKey);
    const mainVariantCount = Number(mainPart?.variantCount) || 0;
    const showOutputVariants = row.baseKey && mainVariantCount > 0;
    const noVariantsMain = row.baseKey && mainVariantCount === 0;
    const selectedOutputIds = (row.selectedPartIds || []).map(String);
    const excludedOutputIds = (row.excludedPartIds || []).map(String);
    const variantItems = row.variantItems || [];
    const manualAssignments = row.manualInputAssignments || {};
    const hasAnyOutputAssignments = Object.values(manualAssignments).some(
      (list) => list?.length > 0,
    );
    const hasDragDrop = selectedOutputIds.length > 0;

    const allInputItems = resolveAllInputVariants();
    const hasInputItems = allInputItems.length > 0;

    // Output variant label helper
    const outputVariantLabel = (outId) => {
      const doc = partDocById(outId, variantItems, partsCatalog);
      return doc ? partInputLabel(doc) : outId;
    };

    // Assigned input item label helper
    const assignedItemLabel = (assignment) => {
      const doc = partDocById(assignment.partId, [], partsCatalog);
      if (doc) {
        const code = doc.code || "";
        const v = partVariantLabel(doc);
        if (code && v && v !== "—") return `${code} (${v})`;
        return code || partInputLabel(doc);
      }
      const entry = activeInputs[assignment.entryIdx];
      return entry ? `Girdi ${assignment.entryIdx + 1}` : assignment.partId;
    };

    const renderOutputWastageMini = (outId) => {
      const settings = getOutputSettings(row, outId);
      const materials = inputMaterialsForOutputVariant(
        outId,
        manualAssignments,
        activeInputs,
        partsCatalog,
        mainPartById,
      );
      const hasMaterials = materials.length > 0;
      const weightCalc = computeOutputEquivalentWeight({
        outputPartId: outId,
        inputs: activeInputs,
        manualAssignments,
        catalog: partsCatalog,
        mainPartById,
        cycleQuantity: settings.cycleQuantity,
        wastage: settings.wastage,
      });
      const displayWeight = resolveOutputDisplayWeight({
        settings,
        weightCalc,
      });
      const eqUnit =
        displayWeight.unit ||
        inferEquivalentUnitSymbol({
          outputPartId: outId,
          inputs: activeInputs,
          manualAssignments,
          catalog: partsCatalog,
          mainPartById,
          weightCalc,
        });
      const wastageNum = Math.max(0, Number(settings.wastage) || 0);
      return (
        <div className="assign-dnd-output__metrics assign-dnd-output__metrics--compact">
          {hasMaterials ? (
            <div className="assign-dnd-output__weight-row">
              <span className="assign-dnd-output__weight-label">Ağırlık</span>
              {displayWeight.net != null ? (
                <MetricAmount
                  className="assign-dnd-output__weight-mini"
                  value={displayWeight.net}
                  unit={eqUnit}
                  title={
                    displayWeight.source === "saved"
                      ? `Kayıtlı ağırlık (envanter ağacı): ${formatEquivalentWeight(displayWeight.net, eqUnit)}`
                      : eqUnit
                        ? `Hesaplanan: ${formatEquivalentWeight(displayWeight.net, eqUnit)}`
                        : "Hammadde karşılığından hesaplanan çıktı ağırlığı"
                  }
                />
              ) : (
                <span
                  className="assign-dnd-output__weight-mini assign-dnd-output__weight-mini--na"
                  title="Atanan girdilerde karşılık değeri tanımlı değil"
                >
                  —
                </span>
              )}
            </div>
          ) : null}
          <div className="assign-dnd-output__wastage-mini">
            {wastageNum > 0 ? (
              <MetricAmount
                className="assign-dnd-output__wastage-badge"
                value={wastageNum}
                unit={eqUnit}
                title={
                  eqUnit
                    ? `Fire: ${formatEquivalentWeight(wastageNum, eqUnit)}`
                    : "Fire"
                }
              />
            ) : eqUnit ? (
              <span
                className="assign-dnd-output__wastage-unit-hint"
                title="Fire birimi"
              >
                {eqUnit}
              </span>
            ) : null}
            <IconButton
              icon={<FiMinusCircle size={14} />}
              label="Fire"
              appearance="subtle"
              shape="circle"
              className="assign-dnd-output__wastage-btn"
              onClick={(e) => {
                e.stopPropagation();
                setWastageModal({
                  open: true,
                  outputPartId: outId,
                  draft: String(settings.wastage ?? "0"),
                  unitSymbol: eqUnit,
                });
              }}
            />
          </div>
        </div>
      );
    };

    const renderDragDropSection = () => {
      const DND_PAGE_SIZE = 15;
      const visibleInputItems = showAssigned
        ? allInputItems
        : allInputItems.filter(
            (item) => !isInputKeyAssigned(item.key, manualAssignments),
          );

      const inputQ = String(dndInputQuery || "")
        .trim()
        .toLocaleLowerCase("tr");
      const outputQ = String(dndOutputQuery || "")
        .trim()
        .toLocaleLowerCase("tr");

      const filteredInputItems = inputQ
        ? visibleInputItems.filter((it) =>
            `${it.fullLabel || ""} ${it.entryLabel || ""}`
              .toLocaleLowerCase("tr")
              .includes(inputQ),
          )
        : visibleInputItems;
      const allFilteredSelected =
        filteredInputItems.length > 0 &&
        filteredInputItems.every((it) => selectedInputKeys.has(it.key));
      const inputTotal = filteredInputItems.length;
      const inputTotalPages = Math.max(
        1,
        Math.ceil(inputTotal / DND_PAGE_SIZE),
      );
      const safeInputPage = Math.min(
        Math.max(1, dndInputPage),
        inputTotalPages,
      );
      const pagedInputItems = filteredInputItems.slice(
        (safeInputPage - 1) * DND_PAGE_SIZE,
        safeInputPage * DND_PAGE_SIZE,
      );

      const filteredOutputIds = outputQ
        ? selectedOutputIds.filter((id) =>
            outputVariantLabel(id).toLocaleLowerCase("tr").includes(outputQ),
          )
        : selectedOutputIds;
      const outputTotal = filteredOutputIds.length;
      const outputTotalPages = Math.max(
        1,
        Math.ceil(outputTotal / DND_PAGE_SIZE),
      );
      const safeOutputPage = Math.min(
        Math.max(1, dndOutputPage),
        outputTotalPages,
      );
      const pagedOutputIds = filteredOutputIds.slice(
        (safeOutputPage - 1) * DND_PAGE_SIZE,
        safeOutputPage * DND_PAGE_SIZE,
      );

      dndMetaRef.current = {
        outputPage: safeOutputPage,
        outputTotalPages: outputTotalPages,
        inputPage: safeInputPage,
        inputTotalPages: inputTotalPages,
      };

      return (
        <div
          className="assign-dnd-section"
          onDragOver={trackDndPointer}
          onDragEnd={handleDndDragEnd}
        >
          {/* Options toolbar */}
          <div className="assign-dnd-toolbar">
            <div className="assign-dnd-toolbar__actions">
              <Button
                appearance="primary-subtle"
                icon={<FiZap size={14} />}
                onClick={autoAssignAll}
                disabled={!hasInputItems}
              >
                Otomatik Ata
              </Button>
              <Button
                appearance="warning-subtle"
                icon={
                  showAssigned ? <FiEyeOff size={14} /> : <FiEye size={14} />
                }
                onClick={() => {
                  setShowAssigned((v) => !v);
                  setDndInputPage(1);
                }}
              >
                {showAssigned ? "Atananları Gizle" : "Atananları Göster"}
              </Button>
            </div>
          </div>

          {/* Two-panel layout */}
          <div className="assign-dnd-panels">
            {/* Left panel: input variants */}
            <div className="assign-dnd-panel assign-dnd-panel--inputs">
              <div className="assign-dnd-panel__header">
                <div className="assign-dnd-panel__head-left">
                  <span className="assign-dnd-panel__title">Girdiler</span>
                  <span className="assign-dnd-panel__count">
                    {inputTotal} / {allInputItems.length}
                  </span>
                </div>
                <div className="assign-dnd-panel__head-right">
                  <div className="assign-dnd-panel__head-actions">
                    {selectedInputKeys.size > 0 && (
                      <>
                        <Button
                          appearance="primary-subtle"
                          icon={<FiLink size={14} />}
                          onClick={() =>
                            setBulkAssignModal({ open: true, targetIds: [] })
                          }
                        >
                          Toplu Ata
                        </Button>
                        <Button
                          appearance="danger-subtle"
                          onClick={() => {
                            setSelectedInputKeys(new Set());
                            setBulkAssignModal({ open: false, targetIds: [] });
                          }}
                        >
                          İptal
                        </Button>
                      </>
                    )}
                    <Button
                      appearance="primary-subtle"
                      disabled={!filteredInputItems.length}
                      onClick={() => {
                        setSelectedInputKeys((prev) => {
                          const next = new Set(prev);
                          if (allFilteredSelected) {
                            for (const it of filteredInputItems)
                              next.delete(it.key);
                          } else {
                            for (const it of filteredInputItems)
                              next.add(it.key);
                          }
                          return next;
                        });
                      }}
                    >
                      {allFilteredSelected ? "Seçimi kaldır" : "Tümünü seç"}
                    </Button>
                  </div>
                  <Input
                    className="assign-dnd-search"
                    placeholder="Ara…"
                    value={dndInputQuery}
                    onChange={(v) => {
                      setDndInputQuery(v);
                      setDndInputPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="assign-dnd-panel__content">
                <div
                  className={`assign-dnd-panel__body${inputTotal >= DND_PAGE_SIZE ? " assign-dnd-panel__body--scroll" : ""}`}
                  ref={inputPanelBodyRef}
                  onDragOver={trackDndPointer}
                >
                  {!hasInputItems ? (
                    <p className="assign-dnd-empty">
                      Önce 2. adımda girdi ekleyin.
                    </p>
                  ) : filteredInputItems.length === 0 ? (
                    <p className="assign-dnd-empty">
                      {inputQ
                        ? "Aramanızla eşleşen girdi yok."
                        : "Tüm girdiler atandı."}{" "}
                      <button
                        type="button"
                        className="assign-dnd-link"
                        onClick={() => setShowAssigned(true)}
                      >
                        Hepsini göster
                      </button>
                    </p>
                  ) : (
                    pagedInputItems.map((item) => {
                      const isSelected = selectedInputKeys.has(item.key);
                      const isAssigned = isInputKeyAssigned(
                        item.key,
                        manualAssignments,
                      );
                      return (
                        <div
                          key={item.key}
                          className={`assign-dnd-item${isSelected ? " assign-dnd-item--selected" : ""}${isAssigned ? " assign-dnd-item--assigned" : ""}`}
                          draggable
                          onDragStart={(e) =>
                            handleInputDragStart(e, item, allInputItems)
                          }
                          onDragEnd={handleDndDragEnd}
                        >
                          <Checkbox
                            value={item.key}
                            label=""
                            size="small"
                            isChecked={isSelected}
                            onChange={() => toggleInputKeySelection(item.key)}
                          />
                          <div className="assign-dnd-item__labels">
                            <span className="assign-dnd-item__entry">
                              {item.fullLabel || item.entryLabel}
                            </span>
                          </div>
                          {isAssigned && (
                            <Lozenge appearance="success-subtle" isBold>
                              ✓
                            </Lozenge>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {inputTotal >= DND_PAGE_SIZE && (
                  <div className="assign-dnd-panel__pagination">
                    <Pagination
                      totalItems={inputTotal}
                      totalPerPage={DND_PAGE_SIZE}
                      currentPage={safeInputPage}
                      options={{
                        selectQuantity: false,
                        simpleCounting: true,
                        skipPrevious: true,
                        skipNext: true,
                        previous: true,
                        next: true,
                      }}
                      onPageChange={({ pagination }) => {
                        setDndInputPage(pagination?.currentPage || 1);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right panel: output variants (drop zones) */}
            <div className="assign-dnd-panel assign-dnd-panel--outputs">
              <div className="assign-dnd-panel__header">
                <div className="assign-dnd-panel__head-left">
                  <span className="assign-dnd-panel__title">
                    Çıktı Varyantları
                  </span>
                  <span className="assign-dnd-panel__count">{outputTotal}</span>
                </div>
                <div className="assign-dnd-panel__head-right">
                  <div className="assign-dnd-panel__head-actions">
                    <Button
                      appearance="primary-subtle"
                      icon={<FiMinusCircle size={14} />}
                      disabled={!selectedOutputIds.length}
                      onClick={() => {
                        const ids = selectedOutputIds
                          .map(String)
                          .filter(Boolean);
                        const rowOut = row;
                        const unitSymbol = ids.length
                          ? inferEquivalentUnitSymbol({
                              outputPartId: ids[0],
                              inputs: activeInputs,
                              manualAssignments:
                                rowOut.manualInputAssignments || {},
                              catalog: partsCatalog,
                              mainPartById,
                            })
                          : "";
                        setWastageBulkModal({
                          open: true,
                          draft: "0",
                          targetIds: ids,
                          unitSymbol,
                        });
                      }}
                    >
                      Toplu Fire
                    </Button>
                    <Button
                      appearance="danger-subtle"
                      disabled={!hasAnyOutputAssignments}
                      onClick={unassignAllOutputs}
                    >
                      Tümünü kaldır
                    </Button>
                  </div>
                  <Input
                    className="assign-dnd-search"
                    placeholder="Ara…"
                    value={dndOutputQuery}
                    onChange={(v) => {
                      setDndOutputQuery(v);
                      setDndOutputPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="assign-dnd-panel__content">
                <div
                  className={`assign-dnd-panel__body${outputTotal >= DND_PAGE_SIZE ? " assign-dnd-panel__body--scroll" : ""}`}
                  ref={outputPanelBodyRef}
                  onDragOver={trackDndPointer}
                >
                  {selectedOutputIds.length === 0 ? (
                    <p className="assign-dnd-empty">
                      Önce yukarıdan çıktı parçası seçin.
                    </p>
                  ) : pagedOutputIds.length === 0 ? (
                    <p className="assign-dnd-empty">
                      {outputQ
                        ? "Aramanızla eşleşen çıktı yok."
                        : "Çıktı bulunamadı."}
                    </p>
                  ) : (
                    pagedOutputIds.map((outId) => {
                      const assigned = manualAssignments[outId] || [];
                      const isOver = dragOverOutputId === outId;
                      const hasAssigned = assigned.length > 0;
                      return (
                        <div
                          key={outId}
                          className={`assign-dnd-output${hasAssigned ? " assign-dnd-output--assigned" : ""}${isOver ? " assign-dnd-output--over" : ""}`}
                          onDragOver={(e) => handleOutputDragOver(e, outId)}
                          onDragLeave={() => setDragOverOutputId(null)}
                          onDrop={(e) => handleOutputDrop(e, outId)}
                          onDragEnd={handleDndDragEnd}
                        >
                          <div className="assign-dnd-output__card">
                            <div className="assign-dnd-output__head">
                              <div className="assign-dnd-output__label">
                                {outputVariantLabel(outId)}
                              </div>
                              <div className="assign-dnd-output__head-actions">
                                {hasAssigned && (
                                  <IconButton
                                    icon={<FiX size={14} />}
                                    label="Atamayı kaldır"
                                    appearance="danger-subtle"
                                    shape="circle"
                                    className="assign-dnd-output__clear"
                                    onClick={() => unassignAllFromOutput(outId)}
                                  />
                                )}
                                <IconButton
                                  icon={<FiTrash2 size={14} />}
                                  label="Varyantı kullanma"
                                  appearance="danger-subtle"
                                  shape="circle"
                                  className="assign-dnd-output__exclude"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    excludeOutputVariant(outId);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="assign-dnd-output__chips">
                              {assigned.map((a) => (
                                <span
                                  key={`${a.entryIdx}::${a.partId}`}
                                  className="assign-dnd-chip"
                                  title={assignedItemLabel(a)}
                                >
                                  {assignedItemLabel(a)}
                                  <input
                                    type="number"
                                    className="assign-dnd-chip__ratio-input form-control form-control-sm ms-1 d-inline-block px-1 py-0 text-center"
                                    step="any"
                                    min="0"
                                    style={{
                                      width: "45px",
                                      height: "18px",
                                      fontSize: "11px",
                                      borderRadius: "3px",
                                      border: "1px solid #cbd5e1",
                                    }}
                                    value={getEntryConsumptionRatio(
                                      activeInputs[a.entryIdx],
                                      a.partId,
                                    )}
                                    onChange={(e) =>
                                      updateEntryConsumptionForPart(
                                        a.entryIdx,
                                        a.partId,
                                        e.target.value,
                                      )
                                    }
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                    title="Karşılık (Kullanım Oranı)"
                                  />
                                  <button
                                    type="button"
                                    className="assign-dnd-chip__remove"
                                    title="Kaldır"
                                    onClick={() =>
                                      unassignInputFromOutput(outId, a.partId)
                                    }
                                  >
                                    <FiX size={11} />
                                  </button>
                                </span>
                              ))}
                              {!hasAssigned && (
                                <span className="assign-dnd-output__placeholder">
                                  Girdiyi buraya sürükleyin
                                </span>
                              )}
                            </div>
                            {renderOutputWastageMini(outId)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {outputTotal >= DND_PAGE_SIZE && (
                  <div className="assign-dnd-panel__pagination">
                    <Pagination
                      totalItems={outputTotal}
                      totalPerPage={DND_PAGE_SIZE}
                      currentPage={safeOutputPage}
                      options={{
                        selectQuantity: false,
                        simpleCounting: true,
                        skipPrevious: true,
                        skipNext: true,
                        previous: true,
                        next: true,
                      }}
                      onPageChange={({ pagination }) => {
                        setDndOutputPage(pagination?.currentPage || 1);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    const excludedPanelKey = `output-excluded-${activePartIdx}`;
    const renderUnusedVariantsPanel = () => {
      if (!showOutputVariants || row.variantLoading || outputVariantsLoading) {
        return null;
      }

      const open = isSummaryOpen(excludedPanelKey);

      return (
        <div className="assign-output-unused mb-3">
          <button
            type="button"
            className="assign-output-unused__toggle"
            aria-expanded={open}
            onClick={() => toggleSummaryOpen(excludedPanelKey)}
          >
            <span className="assign-output-unused__title">
              Kullanılmayan varyantlar
            </span>
            <span className="assign-output-unused__count">
              {excludedOutputIds.length}
            </span>
            <span className="assign-output-unused__chevron" aria-hidden>
              {open ? (
                <FiChevronDown size={16} />
              ) : (
                <FiChevronRight size={16} />
              )}
            </span>
          </button>
          {open ? (
            excludedOutputIds.length ? (
              <ul className="assign-output-unused__list">
                {excludedOutputIds.map((vid) => {
                  const doc = partDocById(vid, variantItems, partsCatalog);
                  return (
                    <li key={vid} className="assign-output-unused__item">
                      <span className="assign-output-unused__label">
                        {doc ? partInputLabel(doc) : vid}
                      </span>
                      <IconButton
                        icon={<FiRotateCcw size={14} />}
                        label="Geri al"
                        appearance="primary-subtle"
                        shape="circle"
                        onClick={() => restoreOutputVariant(vid)}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="assign-output-unused__empty">
                Kullanılmayan varyant yok. Çıktı kartındaki çöp kutusu ile
                buraya taşıyabilirsiniz.
              </p>
            )
          ) : null}
        </div>
      );
    };

    return (
      <div className="assign-section">
        {renderAssignmentValidationPanel()}
        <Select
          className="mb-3"
          label="Çıktı parça"
          required
          unique
          searchInDropdown
          searchPlaceholder="Ara (kod veya ad)…"
          onDropdownSearchChange={handleOutputMainSearch}
          options={outputMainSelectOptions}
          selected={row.baseKey ? [String(row.baseKey)] : []}
          onSelected={(values) => {
            applyMainPartToOutput(values?.[0]?.value || "");
          }}
          placeholder="— Çıktı ana parça —"
        />
        {noVariantsMain && (
          <p className="text-muted small mb-2">
            Varyant yok; ana parça üretim çıktısı olarak kullanılır.
          </p>
        )}
        {showOutputVariants && (
          <p className="text-muted small mb-2">
            {row.variantLoading || outputVariantsLoading
              ? "Tüm çıktı varyantları yükleniyor…"
              : `${selectedOutputIds.length} aktif varyant${excludedOutputIds.length ? ` · ${excludedOutputIds.length} kullanılmıyor` : ""}`}
          </p>
        )}
        {renderUnusedVariantsPanel()}

        {/* Drag-drop assignment panel */}
        {hasDragDrop && renderDragDropSection()}
      </div>
    );
  };

  const toggleVariantSection = (partId) =>
    setVariantSectionOpen((prev) => {
      const key = String(partId);
      // Default is open (undefined / true); only explicit false is collapsed.
      const currentlyOpen = prev[key] !== false;
      return { ...prev, [key]: !currentlyOpen };
    });

  const renderVariantSettingsStep = () => {
    const row = activeOutput;
    const selectedList = (row.selectedPartIds || []).map(String);
    const variantItems = row.variantItems || [];
    const variantIssues = collectVariantSettingsIssues(row, partsCatalog);
    const doneCount = selectedList.filter((id) =>
      isVariantSettingsComplete(row, id),
    ).length;

    if (!selectedList.length) {
      return (
        <div className="assign-section">
          <p className="text-muted small mb-0">
            Önce 3. adımda çıktı parçası seçin.
          </p>
        </div>
      );
    }

    return (
      <div className="assign-section">
        {variantIssues.length > 0 && (
          <div className="alert-inline alert-inline--danger mb-3" role="alert">
            <strong className="d-block mb-2">Eksik varyant ayarları</strong>
            <ul className="mb-0 ps-3">
              {variantIssues.map((issue, i) => (
                <li key={i}>{issue.message}</li>
              ))}
            </ul>
          </div>
        )}
        {selectedList.length > 0 && (
          <p className="text-muted small mb-3">
            {doneCount} / {selectedList.length} varyant tamamlandı
          </p>
        )}
        <div className="variant-settings-list">
          {selectedList.map((outId, idx) => {
            const outDoc = partDocById(outId, variantItems, partsCatalog);
            const settings = getOutputSettings(row, outId);
            const label = outDoc
              ? partInputLabel(outDoc)
              : `Varyant ${idx + 1}`;
            // Default open so PLC measurement axes are visible without extra clicks
            const open = variantSectionOpen[String(outId)] !== false;
            const done = isVariantSettingsComplete(row, outId);
            const cq = Number(settings.cycleQuantity);
            const nt = Number(settings.netCycleTime);
            const cycleHint = !Number.isNaN(nt) && nt > 1 ? `${nt} sn` : null;
            const showCycleQtyHint =
              String(settings.cycleQuantity ?? "").trim() &&
              (Number.isNaN(cq) || cq < 1);
            const showNetCycleHint =
              String(settings.netCycleTime ?? "").trim() &&
              (Number.isNaN(nt) || nt <= 1);
            const axesList = settings.measurementAxes || [];
            const showDeviceHint =
              !!settings.isFinalProduct &&
              !String(settings.deviceModel || "").trim();
            const cycleUnit = partUnitLabelForDoc(outDoc, mainPartById);
            return (
              <div
                key={outId}
                className={`variant-settings-card${done ? " variant-settings-card--done" : ""}${open ? " variant-settings-card--open" : ""}`}
              >
                <button
                  type="button"
                  className="variant-settings-card__header"
                  aria-expanded={open}
                  onClick={() => toggleVariantSection(outId)}
                >
                  <span
                    className={`variant-settings-card__status${done ? " variant-settings-card__status--done" : ""}`}
                    aria-hidden
                  >
                    {done ? <FiCheck size={18} strokeWidth={3} /> : idx + 1}
                  </span>
                  <span className="variant-settings-card__title min-w-0">
                    <span className="variant-settings-card__label text-truncate d-block">
                      {label}
                    </span>
                    {cycleHint && (
                      <span className="variant-settings-card__meta">
                        Net çevrim: {cycleHint}
                      </span>
                    )}
                  </span>
                  <span className="variant-settings-card__chevron" aria-hidden>
                    {open ? (
                      <FiChevronDown size={18} />
                    ) : (
                      <FiChevronRight size={18} />
                    )}
                  </span>
                  <span
                    className={`variant-settings-card__badge${done ? " variant-settings-card__badge--done" : ""}`}
                  >
                    {done ? "Tamam" : "Eksik"}
                  </span>
                </button>
                {open && (
                  <div className="variant-settings-card__body">
                    <div className="variant-settings-card__fields">
                      <div className="variant-settings-card__field">
                        <Label>
                          {labelWithUnit("Çevrim miktarı", cycleUnit)}
                        </Label>
                        <div className="assign-qty-with-unit">
                          <Input
                            type="number"
                            min="1"
                            value={settings.cycleQuantity}
                            onChange={(v) =>
                              updateVariantSettings(outId, {
                                cycleQuantity: v,
                              })
                            }
                          />
                          {cycleUnit ? (
                            <span className="assign-qty-with-unit__suffix">
                              {cycleUnit}
                            </span>
                          ) : null}
                        </div>
                        <span
                          className={`variant-settings-card__hint${showCycleQtyHint ? "" : " variant-settings-card__hint--hidden"}`}
                          aria-hidden={!showCycleQtyHint}
                        >
                          Tamamlamak için en az 1 girin
                        </span>
                      </div>
                      <div className="variant-settings-card__field">
                        <Label>Net çevrim (sn)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          value={settings.netCycleTime ?? ""}
                          onChange={(v) =>
                            updateVariantSettings(outId, { netCycleTime: v })
                          }
                        />
                        <span
                          className={`variant-settings-card__hint${showNetCycleHint ? "" : " variant-settings-card__hint--hidden"}`}
                          aria-hidden={!showNetCycleHint}
                        >
                          Tamamlamak için 1 sn&apos;den büyük girin
                        </span>
                      </div>
                    </div>

                    <p className="variant-settings-card__section-title">
                      PLC ölçü değişkenleri
                    </p>
                    <div className="variant-settings-card__dim-rows">
                      {axesList.map((axis, axIdx) => (
                        <div
                          key={axIdx}
                          className="variant-settings-card__dim-row variant-settings-card__dim-row--inline"
                        >
                          <div className="variant-settings-card__dim-group">
                            <span className="variant-settings-card__dim-label">
                              Değişken
                            </span>
                            <div className="variant-settings-card__dim-inputs">
                              <div className="variant-settings-card__dim-value variant-settings-card__dim-value--full">
                                <Input
                                  type="text"
                                  maxLength={64}
                                  placeholder={`r${axIdx}`}
                                  value={axis.payloadKey ?? ""}
                                  onChange={(v) => {
                                    const newAxes = [...axesList];
                                    newAxes[axIdx] = {
                                      ...newAxes[axIdx],
                                      payloadKey: v,
                                    };
                                    updateVariantSettings(outId, {
                                      measurementAxes: newAxes,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="variant-settings-card__dim-group variant-settings-card__dim-group--tol">
                            <span className="variant-settings-card__dim-label">
                              Koşul
                            </span>
                            <Select
                              className="mb-0"
                              unique
                              options={[
                                { value: "eq", label: "eşittir (=)" },
                                { value: "gte", label: "≥ eşik ve üzeri" },
                                { value: "lte", label: "≤ eşik ve altı" },
                              ]}
                              selected={[axis.operator || "eq"]}
                              onSelected={(values) => {
                                const newAxes = [...axesList];
                                newAxes[axIdx] = {
                                  ...newAxes[axIdx],
                                  operator: values?.[0]?.value || "eq",
                                };
                                updateVariantSettings(outId, {
                                  measurementAxes: newAxes,
                                });
                              }}
                            />
                          </div>
                          <div className="variant-settings-card__dim-group">
                            <span className="variant-settings-card__dim-label">
                              Değer
                            </span>
                            <div className="variant-settings-card__dim-inputs">
                              <div className="variant-settings-card__dim-value variant-settings-card__dim-value--full">
                                <Input
                                  type="number"
                                  min="0"
                                  step="any"
                                  placeholder="0"
                                  value={axis.expected ?? ""}
                                  onChange={(v) => {
                                    const newAxes = [...axesList];
                                    newAxes[axIdx] = {
                                      ...newAxes[axIdx],
                                      expected: v,
                                    };
                                    updateVariantSettings(outId, {
                                      measurementAxes: newAxes,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="variant-settings-card__dim-group variant-settings-card__dim-group--tol">
                            <span className="variant-settings-card__dim-label">
                              Tolerans (%)
                            </span>
                            <div className="variant-settings-card__dim-inputs">
                              <div className="variant-settings-card__dim-value variant-settings-card__dim-value--full">
                                <Input
                                  type="number"
                                  min="0"
                                  step="any"
                                  placeholder="0"
                                  value={axis.tolerance ?? ""}
                                  onChange={(v) => {
                                    const newAxes = [...axesList];
                                    newAxes[axIdx] = {
                                      ...newAxes[axIdx],
                                      tolerance: v,
                                    };
                                    updateVariantSettings(outId, {
                                      measurementAxes: newAxes,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          {axesList.length > 1 && (
                            <IconButton
                              icon={<FiTrash2 />}
                              label="Değişkeni sil"
                              appearance="danger-subtle"
                              shape="circle"
                              onClick={() => {
                                const newAxes = axesList.filter(
                                  (_, i) => i !== axIdx,
                                );
                                updateVariantSettings(outId, {
                                  measurementAxes: newAxes,
                                });
                              }}
                            />
                          )}
                        </div>
                      ))}
                      <Button
                        appearance="primary-subtle"
                        icon={<FiPlus />}
                        onClick={() => {
                          const newAxes = [
                            ...axesList,
                            {
                              payloadKey: `r${axesList.length}`,
                              expected: "",
                              tolerance: "",
                              operator: "eq",
                            },
                          ];
                          updateVariantSettings(outId, {
                            measurementAxes: newAxes,
                          });
                        }}
                      >
                        Değişken Ekle
                      </Button>
                    </div>
                    <div className="variant-settings-card__final">
                      <Checkbox
                        label="Nihai ürün"
                        size="small"
                        isChecked={!!settings.isFinalProduct}
                        onChange={({ checked }) =>
                          updateVariantSettings(outId, {
                            isFinalProduct: checked,
                            deviceModel: checked ? settings.deviceModel : "",
                          })
                        }
                      />
                      {settings.isFinalProduct && (
                        <div className="variant-settings-card__field mt-2 mb-0">
                          <Select
                            className="mb-0"
                            label="Cihaz modeli"
                            unique
                            options={modelOptions}
                            selected={
                              settings.deviceModel
                                ? [String(settings.deviceModel)]
                                : [""]
                            }
                            onSelected={(values) => {
                              const v = values?.[0]?.value || "";
                              updateVariantSettings(outId, { deviceModel: v });
                            }}
                          />
                          <span
                            className={`variant-settings-card__hint${showDeviceHint ? "" : " variant-settings-card__hint--hidden"}`}
                            aria-hidden={!showDeviceHint}
                          >
                            Nihai ürün için cihaz modeli seçin
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const modelLabelById = (id) => {
    if (!id) return "—";
    const m = models.find((x) => String(x._id) === String(id));
    return m ? `${m.code} — ${m.name}` : "—";
  };

  const updatePartStatus = (partIdx, isActive) =>
    setFormData((s) => {
      const parts = [...s.parts];
      parts[partIdx] = {
        ...parts[partIdx],
        output: { ...parts[partIdx].output, isActive },
      };
      return { ...s, parts };
    });

  const goEditPart = (partIdx, step = 2) => {
    setActivePartIdx(partIdx);
    setActiveStep(step);
  };

  const renderSummaryCollapsible = (key, title, icon, meta, children) => {
    const open = isSummaryOpen(key);
    return (
      <div
        className={`assign-summary__collapse${open ? " assign-summary__collapse--open" : ""}`}
      >
        <button
          type="button"
          className="assign-summary__collapse-btn"
          aria-expanded={open}
          onClick={() => toggleSummaryOpen(key)}
        >
          <span className="assign-summary__collapse-title">
            {icon}
            <span>{title}</span>
            {meta != null && meta !== "" ? (
              <span className="assign-summary__collapse-meta">{meta}</span>
            ) : null}
          </span>
          <span className="assign-summary__collapse-chevron" aria-hidden>
            {open ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          </span>
        </button>
        {open ? (
          <div className="assign-summary__collapse-body">{children}</div>
        ) : null}
      </div>
    );
  };

  const renderSummaryStep = () => {
    const machine = machines.find(
      (m) => String(m._id) === String(formData.machine),
    );
    const machineLabel = machine ? `${machine.code} — ${machine.name}` : "—";
    const machineKey = "summary-machine";
    const partsListKey = "summary-parts-list";

    return (
      <div className="assign-section assign-summary">
        <p className="assign-section__desc mb-3">
          Kaydetmeden önce makine ve tüm parça atamalarını kontrol edin.
        </p>
        {/* 
        {renderSummaryCollapsible(
          machineKey,
          "Makine",
          <FiServer size={14} />,
          `${formData.parts.length} parça`,
          <div className="assign-summary__machine-inline">
            <strong>{machineLabel}</strong>
          </div>,
        )} */}

        {renderSummaryCollapsible(
          partsListKey,
          "Parça atamaları",
          <FiPackage size={14} />,
          `${formData.parts.length} kayıt`,
          <div className="assign-summary__parts">
            {formData.parts.map((part, partIdx) => {
              const out = part.output;
              const outMain = mainPartById(out.baseKey);
              const partTitle = outMain
                ? partBaseLabel(outMain)
                : `Parça ${partIdx + 1}`;
              const partKey = `summary-part-${partIdx}`;
              const assignmentIssues = collectAssignmentValidationIssues(
                out,
                part.inputs,
                partsCatalog,
                mainPartById,
              );
              const variantIssues = collectVariantSettingsIssues(
                out,
                partsCatalog,
              );
              const partOk =
                !assignmentIssues.length &&
                !variantIssues.length &&
                !!out.baseKey;
              const configuredInputs = part.inputs.filter((e) =>
                inputMainConfigured(e),
              );
              const selectedVariants = (out.selectedPartIds || []).map(String);
              const partOpen = isSummaryOpen(partKey);

              return (
                <div
                  key={part.id}
                  className={`assign-summary__part${partOk ? " assign-summary__part--ok" : " assign-summary__part--warn"}${partOpen ? " assign-summary__part--open" : ""}`}
                >
                  <div className="assign-summary__part-head">
                    <button
                      type="button"
                      className="assign-summary__part-toggle"
                      aria-expanded={partOpen}
                      onClick={() => toggleSummaryOpen(partKey)}
                    >
                      <span
                        className="assign-summary__collapse-chevron"
                        aria-hidden
                      >
                        {partOpen ? (
                          <FiChevronDown size={16} />
                        ) : (
                          <FiChevronRight size={16} />
                        )}
                      </span>
                      <div className="assign-summary__part-title-wrap">
                        <span className="assign-summary__part-num">
                          {partIdx + 1}
                        </span>
                        <div>
                          <h3 className="assign-summary__part-title">
                            {partTitle}
                          </h3>
                          <div className="assign-summary__part-badges">
                            <Lozenge
                              appearance={
                                partOk ? "success-subtle" : "warning-subtle"
                              }
                              isBold
                            >
                              {partOk ? "Hazır" : "Eksik"}
                            </Lozenge>
                            <Lozenge
                              appearance={
                                out.isActive !== false
                                  ? "success-subtle"
                                  : "default-subtle"
                              }
                            >
                              {out.isActive !== false ? "Aktif" : "Pasif"}
                            </Lozenge>
                          </div>
                        </div>
                      </div>
                    </button>
                    <div
                      className="assign-summary__part-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select
                        className="assign-summary__status-select mb-0"
                        label=""
                        unique
                        options={[
                          { value: "1", label: "Aktif" },
                          { value: "0", label: "Pasif" },
                        ]}
                        selected={[out.isActive !== false ? "1" : "0"]}
                        onSelected={(values) =>
                          updatePartStatus(partIdx, values?.[0]?.value !== "0")
                        }
                      />
                      <Button
                        appearance="warning-subtle"
                        icon={<FiEdit2 />}
                        onClick={() => goEditPart(partIdx, 2)}
                      >
                        Düzenle
                      </Button>
                    </div>
                  </div>

                  {partOpen && (
                    <div className="assign-summary__part-body">
                      {(assignmentIssues.length > 0 ||
                        variantIssues.length > 0) && (
                        <div className="alert-inline alert-inline--danger mb-3">
                          <ul className="mb-0 ps-3 small">
                            {[...assignmentIssues, ...variantIssues].map(
                              (issue, i) => (
                                <li key={i}>{issue.message}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}

                      {renderSummaryCollapsible(
                        `${partKey}-inputs`,
                        "Girdiler",
                        <FiList size={14} />,
                        configuredInputs.length
                          ? `${configuredInputs.length} kalem`
                          : "Yok",
                        configuredInputs.length ? (
                          <ul className="assign-summary__list mb-0">
                            {configuredInputs.map((entry, i) => {
                              const { title, meta } = entryInputSummary(
                                entry,
                                i,
                                mainPartById,
                                partsCatalog,
                              );
                              return (
                                <li key={i}>
                                  <strong>{title}</strong>
                                  <span className="text-muted"> — {meta}</span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-muted small mb-0">
                            Girdi tanımlı değil
                          </p>
                        ),
                      )}

                      {renderSummaryCollapsible(
                        `${partKey}-output`,
                        "Çıktı",
                        <FiPackage size={14} />,
                        selectedVariants.length
                          ? `${selectedVariants.length} varyant`
                          : out.baseKey
                            ? "Varyantsız"
                            : "Seçilmedi",
                        !out.baseKey ? (
                          <p className="text-muted small mb-0">
                            Çıktı ana parça seçilmedi
                          </p>
                        ) : selectedVariants.length ? (
                          <ul className="assign-summary__list mb-0">
                            {selectedVariants.map((vid) => {
                              const doc = partDocById(
                                vid,
                                out.variantItems || [],
                                partsCatalog,
                              );
                              return (
                                <li key={vid}>
                                  {doc ? partInputLabel(doc) : vid}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-muted small mb-0">
                            {outMain ? partBaseLabel(outMain) : "—"}{" "}
                            (varyantsız)
                          </p>
                        ),
                      )}

                      {selectedVariants.length > 0 &&
                        renderSummaryCollapsible(
                          `${partKey}-variants`,
                          "Varyant ayarları",
                          <FiClock size={14} />,
                          `${selectedVariants.length} varyant`,
                          (() => {
                            const {
                              total,
                              page,
                              items: pagedVariants,
                            } = paginateVariantTable(
                              selectedVariants,
                              summaryVariantPageByPart[partIdx] || 1,
                            );
                            return (
                              <VariantTablePanel
                                total={total}
                                page={page}
                                wrapClass="assign-summary__variant-table-wrap"
                                onPageChange={(nextPage) =>
                                  setSummaryVariantPageByPart((prev) => ({
                                    ...prev,
                                    [partIdx]: nextPage,
                                  }))
                                }
                              >
                                <table className="assign-summary__variant-table">
                                  <thead>
                                    <tr>
                                      <th>Varyant</th>
                                      <th>Üretim miktarı</th>
                                      <th>Karşılık ağırlığı</th>
                                      <th>Fire</th>
                                      <th>Net çevrim</th>
                                      <th>Eksenler</th>
                                      <th>Nihai ürün</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pagedVariants.map((vid) => {
                                      const doc = partDocById(
                                        vid,
                                        out.variantItems || [],
                                        partsCatalog,
                                      );
                                      const settings = getOutputSettings(
                                        out,
                                        vid,
                                      );
                                      const done = isVariantSettingsComplete(
                                        out,
                                        vid,
                                      );
                                      const cycleUnit = partUnitLabelForDoc(
                                        doc,
                                        mainPartById,
                                      );
                                      const weightCalc =
                                        computeOutputEquivalentWeight({
                                          outputPartId: vid,
                                          inputs: part.inputs,
                                          manualAssignments:
                                            out.manualInputAssignments || {},
                                          catalog: partsCatalog,
                                          mainPartById,
                                          cycleQuantity: settings.cycleQuantity,
                                          wastage: settings.wastage,
                                        });
                                      const wastageNum = Math.max(
                                        0,
                                        Number(settings.wastage) || 0,
                                      );
                                      const eqUnit = inferEquivalentUnitSymbol({
                                        outputPartId: vid,
                                        inputs: part.inputs,
                                        manualAssignments:
                                          out.manualInputAssignments || {},
                                        catalog: partsCatalog,
                                        mainPartById,
                                        weightCalc,
                                      });
                                      return (
                                        <tr key={vid}>
                                          <td>
                                            <span className="d-flex align-items-center gap-2">
                                              {done ? (
                                                <FiCheck
                                                  size={14}
                                                  className="text-success"
                                                />
                                              ) : null}
                                              {doc ? partInputLabel(doc) : vid}
                                            </span>
                                          </td>
                                          <td>
                                            {quantityWithUnit(
                                              settings.cycleQuantity,
                                              cycleUnit,
                                            )}
                                          </td>
                                          <td>
                                            {weightCalc.ok ? (
                                              <MetricAmount
                                                value={weightCalc.net}
                                                unit={eqUnit}
                                              />
                                            ) : (
                                              "—"
                                            )}
                                          </td>
                                          <td>
                                            <MetricAmount
                                              value={wastageNum}
                                              unit={eqUnit}
                                            />
                                          </td>
                                          <td>
                                            {settings.netCycleTime
                                              ? `${settings.netCycleTime} sn`
                                              : "—"}
                                          </td>
                                          <td>
                                            {(() => {
                                              const axes =
                                                settings.measurementAxes || [];
                                              const parts = axes
                                                .filter(
                                                  (a) =>
                                                    String(
                                                      a.payloadKey ?? "",
                                                    ).trim() &&
                                                    Number(a.expected) > 0,
                                                )
                                                .map(
                                                  (a) =>
                                                    `${a.payloadKey}=${a.expected}(${a.tolerance}%)`,
                                                );
                                              return parts.length > 0
                                                ? parts.join(", ")
                                                : "—";
                                            })()}
                                          </td>
                                          <td>
                                            {settings.isFinalProduct
                                              ? modelLabelById(
                                                  settings.deviceModel,
                                                )
                                              : "Hayır"}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </VariantTablePanel>
                            );
                          })(),
                        )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>,
        )}
      </div>
    );
  };

  const renderEntryEditModal = () => {
    const idx = entryEditModal.idx;
    if (idx == null || !entryEditModal.open) return null;
    const entry = activeInputs[idx];
    if (!entry) return null;

    const inputMid = entry.inputMainPartId ? String(entry.inputMainPartId) : "";
    const currentRatio = inputMid
      ? getEntryConsumptionRatio(entry, inputMid)
      : "1";
    const mp = inputMid ? mainPartById(inputMid) : null;
    const variantCount = Number(mp?.variantCount) || 0;
    const inputVariantItems =
      inputMid && variantCount > 0
        ? inputVariantsForMain(inputMid, partsCatalog)
        : [];
    const allowed = resolveEntryAllowedPartIds(
      entry,
      partsCatalog,
      mainPartById,
    );
    const allowedSet = new Set(allowed);
    const bulkPickCount = [...entryBulkPickKeys].filter((id) =>
      allowedSet.has(id),
    ).length;
    const inputUnit = partUnitLabel(mp);

    return (
      <Modal
        isOpen={entryEditModal.open}
        onClose={closeEntryEdit}
        width="large"
        className="part-machine-assign"
      >
        <ModalHeader>Girdi {idx + 1}</ModalHeader>
        <ModalBody>
          <Select
            className="mb-3"
            label="Girdi ana parça"
            required
            unique
            searchInDropdown
            searchPlaceholder="Ara (kod veya ad)…"
            onDropdownSearchChange={handleInputMainSearch}
            options={inputMainSelectOptions}
            selected={inputMid ? [inputMid] : []}
            onSelected={(values) => {
              const v = String(values?.[0]?.value || "");
              applyEntryInput(idx, v, currentRatio);
            }}
            placeholder="— Girdi ana parça —"
          />
          {inputMid && variantCount > 0 ? (
            <div className="entry-edit-variants">
              {(() => {
                const {
                  total,
                  page: safePage,
                  items: pagedItems,
                } = paginateVariantTable(inputVariantItems, entryVariantPage);
                const pageIds = pagedItems.map((v) => String(v._id));
                const allowedOnPage = pageIds.filter((id) =>
                  allowedSet.has(id),
                );
                const allPageAllowed =
                  pageIds.length > 0 &&
                  pageIds.every((id) => allowedSet.has(id));
                const allPageBulkPicked =
                  allowedOnPage.length > 0 &&
                  allowedOnPage.every((id) => entryBulkPickKeys.has(id));

                return (
                  <section className="entry-edit-section entry-edit-section--variants">
                    <div className="entry-edit-section__head">
                      <div>
                        <h4 className="entry-edit-section__title mb-0">
                          Varyant kullanımı ve miktar
                        </h4>
                      </div>
                      <div className="entry-edit-stats">
                        <span className="entry-edit-stats__total">
                          {inputVariantItems.length} varyant
                        </span>
                      </div>
                    </div>

                    <div className="entry-edit-toolbar">
                      <div
                        className="entry-edit-toolbar__group entry-edit-toolbar__group--bulk"
                        aria-label="Toplu miktar"
                      >
                        <span className="entry-edit-toolbar__label">
                          {labelWithUnit(
                            "Toplu Tüketim Miktarı Belirle",
                            inputUnit,
                          )}
                        </span>
                        <div className="assign-qty-with-unit entry-edit-toolbar__ratio-wrap">
                          <Input
                            className="entry-edit-toolbar__ratio"
                            type="number"
                            min="0"
                            step="any"
                            value={entryBulkRatio}
                            onChange={setEntryBulkRatio}
                          />
                          {inputUnit ? (
                            <span className="assign-qty-with-unit__suffix">
                              {inputUnit}
                            </span>
                          ) : null}
                        </div>
                        <Button
                          appearance="primary"
                          disabled={bulkPickCount === 0}
                          onClick={() =>
                            applyEntryConsumptionToParts(
                              idx,
                              [...entryBulkPickKeys].filter((id) =>
                                allowedSet.has(id),
                              ),
                              entryBulkRatio,
                            )
                          }
                        >
                          Uygula
                        </Button>
                        <Button
                          appearance="primary-subtle"
                          disabled={!allowedOnPage.length}
                          onClick={() =>
                            setEntryBulkPickIds(
                              allowedOnPage,
                              !allPageBulkPicked,
                            )
                          }
                        >
                          {allPageBulkPicked ? "Seçimi kaldır" : "Tümünü seç"}
                        </Button>
                      </div>
                    </div>

                    {total === 0 ? (
                      <p className="entry-edit-empty">Varyant bulunamadı.</p>
                    ) : (
                      <VariantTablePanel
                        total={total}
                        page={safePage}
                        wrapClass="entry-variant-table-wrap"
                        onPageChange={setEntryVariantPage}
                      >
                        <table className="entry-variant-grid">
                          <colgroup>
                            <col className="entry-variant-grid__col-check" />
                            <col className="entry-variant-grid__col-check" />
                            <col />
                            <col className="entry-variant-grid__col-qty" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th scope="col">Kullan</th>
                              <th scope="col">Toplu</th>
                              <th scope="col">Varyant</th>
                              <th scope="col" className="text-end">
                                {labelWithUnit("Miktar", inputUnit)}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedItems.map((vp) => {
                              const vid = String(vp._id);
                              const isAllowed = allowedSet.has(vid);
                              const isBulkPicked =
                                isAllowed && entryBulkPickKeys.has(vid);
                              const variantRatio = getEntryConsumptionRatio(
                                entry,
                                vid,
                              );
                              const rowUnit = partUnitLabelForDoc(
                                vp,
                                mainPartById,
                              );
                              const rowClass = [
                                isAllowed && "entry-variant-grid__row--active",
                                isBulkPicked && "entry-variant-grid__row--bulk",
                                !isAllowed &&
                                  "entry-variant-grid__row--excluded",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              return (
                                <tr key={vid} className={rowClass || undefined}>
                                  <td className="entry-variant-grid__cell-check">
                                    <span className="entry-variant-grid__check">
                                      <Checkbox
                                        value={`use-${vid}`}
                                        label=""
                                        size="small"
                                        isChecked={isAllowed}
                                        onChange={({
                                          checked: nextChecked,
                                        }) => {
                                          const next = nextChecked
                                            ? [...new Set([...allowed, vid])]
                                            : allowed.filter((x) => x !== vid);
                                          updateEntryAllowedPartIds(idx, next);
                                          if (!nextChecked) {
                                            setEntryBulkPickIds([vid], false);
                                          }
                                        }}
                                      />
                                    </span>
                                  </td>
                                  <td className="entry-variant-grid__cell-check">
                                    <span className="entry-variant-grid__check">
                                      <Checkbox
                                        value={`bulk-${vid}`}
                                        label=""
                                        size="small"
                                        isChecked={isBulkPicked}
                                        disabled={!isAllowed}
                                        onChange={() =>
                                          toggleEntryBulkPick(vid, isAllowed)
                                        }
                                      />
                                    </span>
                                  </td>
                                  <td className="entry-variant-grid__cell-label">
                                    <span className="entry-variant-grid__name">
                                      {partVariantLabel(vp)}
                                    </span>
                                    <span className="entry-variant-grid__code">
                                      {vp.code || "—"}
                                    </span>
                                  </td>
                                  <td className="entry-variant-grid__cell-qty">
                                    <div className="assign-qty-with-unit assign-qty-with-unit--grid">
                                      <Input
                                        className="entry-variant-grid__qty-input"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={variantRatio}
                                        disabled={!isAllowed}
                                        onChange={(v) =>
                                          updateEntryConsumptionForPart(
                                            idx,
                                            vid,
                                            v,
                                          )
                                        }
                                      />
                                      {rowUnit ? (
                                        <span className="assign-qty-with-unit__suffix">
                                          {rowUnit}
                                        </span>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </VariantTablePanel>
                    )}
                  </section>
                );
              })()}
            </div>
          ) : (
            <div className="mt-3">
              <Label>{labelWithUnit("Kullanılan miktar", inputUnit)}</Label>
              <div className="assign-qty-with-unit">
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={currentRatio}
                  disabled={!inputMid}
                  onChange={(v) => applyEntryDefaultConsumption(idx, v)}
                />
                {inputUnit ? (
                  <span className="assign-qty-with-unit__suffix">
                    {inputUnit}
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end">
            <Button appearance="primary" onClick={closeEntryEdit}>
              Tamam
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    );
  };

  // Part tab bar — shown for steps 2–4 only (step 5 is full summary)
  const renderPartTabs = () => {
    if (activeStep < 2 || activeStep > 4) return null;
    return (
      <div className="part-tabs-bar">
        {formData.parts.map((part, idx) => {
          const outMain = mainParts.find(
            (p) => String(p._id) === String(part.output?.baseKey),
          );
          const tabLabel = outMain
            ? outMain.code || outMain.name || `Parça ${idx + 1}`
            : `Parça ${idx + 1}`;
          const isActive = activePartIdx === idx;
          const isDone =
            outputComplete(part.output) &&
            !collectVariantSettingsIssues(part.output, partsCatalog).length;
          return (
            <div
              key={part.id}
              className={`part-tab${isActive ? " part-tab--active" : ""}${isDone ? " part-tab--done" : ""}`}
            >
              <button
                type="button"
                className="part-tab__label"
                onClick={() => setActivePartIdx(idx)}
              >
                {isDone && !isActive && (
                  <FiCheck size={11} strokeWidth={3} className="me-1" />
                )}
                {tabLabel}
              </button>
              {formData.parts.length > 1 && (
                <button
                  type="button"
                  className="part-tab__close"
                  onClick={() => removePart(idx)}
                  title="Parçayı kaldır"
                >
                  <FiX size={11} />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="part-tab part-tab--add"
          onClick={addPart}
          title="Yeni parça ekle"
        >
          <FiPlus size={14} />
        </button>
      </div>
    );
  };

  const activeStepMeta = ASSIGNMENT_STEPS.find((s) => s.id === activeStep);

  const breadcrumbs = [
    { label: "Parça / Makine", url: "/part-machines" },
    { label: isEdit ? "Düzenle" : "Yeni atama" },
  ];

  if (authLoading || loadingInit) {
    return (
      <AppPage
        title="Makine ataması"
        breadcrumbs={breadcrumbs}
        className="part-machine-assign"
      >
        <CardContainer className="assign-page-body">
          <div className="card border-0 assign-wizard-card">
            <p className="text-muted mb-0">Yükleniyor…</p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  const selectedMachine = machines.find(
    (m) => String(m._id) === String(formData.machine),
  );

  return (
    <AppPage
      title={isEdit ? "Makine Ataması Düzenle" : "Yeni Makine Ataması"}
      description="Makine, kayıt, çıktı ve üretim bilgilerini adım adım tanımlayın."
      breadcrumbs={breadcrumbs}
      className="part-machine-assign"
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={mergeConfirmModal.isOpen}
        onClose={() =>
          setMergeConfirmModal({
            isOpen: false,
            machineId: null,
            machineLabel: "",
            recordCount: 0,
          })
        }
        width="small"
      >
        <ModalHeader>Mevcut makine kaydı</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            <strong>{mergeConfirmModal.machineLabel || "Makine"}</strong> için
            zaten <strong>{mergeConfirmModal.recordCount}</strong> parça ataması
            var. Bu kayda eklemek istiyor musunuz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              onClick={confirmMergeExisting}
            >
              Ekle
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setMergeConfirmModal({
                  isOpen: false,
                  machineId: null,
                  machineLabel: "",
                  recordCount: 0,
                })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={bulkAssignModal.open}
        onClose={() => setBulkAssignModal({ open: false, targetIds: [] })}
        width="medium"
      >
        <ModalHeader>Toplu Atama</ModalHeader>
        <ModalBody>
          <Select
            className="mb-3"
            label="Çıktı varyantları"
            placeholder="— Varyant seçin —"
            options={(() => {
              const out = formData.parts[activePartIdx]?.output;
              const ids = (out?.selectedPartIds || []).map(String);
              const items = out?.variantItems || [];
              return ids.map((id) => ({
                value: id,
                label:
                  partInputLabel(partDocById(id, items, partsCatalog)) || id,
              }));
            })()}
            selected={bulkAssignModal.targetIds}
            searchInDropdown
            searchPlaceholder="Çıktı ara…"
            onSelected={(vals) =>
              setBulkAssignModal((s) => ({
                ...s,
                targetIds: (vals || []).map((v) => String(v.value)),
              }))
            }
          />
          <div className="d-flex gap-2">
            <Button
              appearance="primary-subtle"
              onClick={() => {
                const out = formData.parts[activePartIdx]?.output;
                const ids = (out?.selectedPartIds || [])
                  .map(String)
                  .filter(Boolean);
                setBulkAssignModal((s) => ({ ...s, targetIds: ids }));
              }}
            >
              Tümünü seç
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setBulkAssignModal((s) => ({ ...s, targetIds: [] }))
              }
            >
              Seçimi temizle
            </Button>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              icon={<FiLink size={14} />}
              disabled={
                !bulkAssignModal.targetIds.length || !selectedInputKeys.size
              }
              onClick={handleBulkAssign}
            >
              Ata
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() => setBulkAssignModal({ open: false, targetIds: [] })}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={wastageBulkModal.open}
        onClose={() =>
          setWastageBulkModal({
            open: false,
            draft: "0",
            targetIds: [],
            unitSymbol: "",
          })
        }
        width="medium"
      >
        <ModalHeader>Toplu Fire</ModalHeader>
        <ModalBody>
          <Select
            className="mb-3"
            label="Çıktı varyantları"
            placeholder="— Varyant seçin —"
            options={(() => {
              const out = formData.parts[activePartIdx]?.output;
              const ids = (out?.selectedPartIds || []).map(String);
              const items = out?.variantItems || [];
              return ids.map((id) => ({
                value: id,
                label:
                  partInputLabel(partDocById(id, items, partsCatalog)) || id,
              }));
            })()}
            selected={wastageBulkModal.targetIds}
            searchInDropdown
            searchPlaceholder="Çıktı ara…"
            onSelected={(vals) => {
              const targetIds = (vals || []).map((v) => String(v.value));
              const part = formData.parts[activePartIdx];
              const unitSymbol = targetIds.length
                ? inferEquivalentUnitSymbol({
                    outputPartId: targetIds[0],
                    inputs: part?.inputs || [],
                    manualAssignments:
                      part?.output?.manualInputAssignments || {},
                    catalog: partsCatalog,
                    mainPartById,
                  })
                : "";
              setWastageBulkModal((s) => ({
                ...s,
                targetIds,
                unitSymbol,
              }));
            }}
          />
          <div className="d-flex gap-2 mb-3">
            <Button
              appearance="primary-subtle"
              onClick={() => {
                const out = formData.parts[activePartIdx]?.output;
                const part = formData.parts[activePartIdx];
                const ids = (out?.selectedPartIds || [])
                  .map(String)
                  .filter(Boolean);
                const unitSymbol = ids.length
                  ? inferEquivalentUnitSymbol({
                      outputPartId: ids[0],
                      inputs: part?.inputs || [],
                      manualAssignments: out?.manualInputAssignments || {},
                      catalog: partsCatalog,
                      mainPartById,
                    })
                  : "";
                setWastageBulkModal((s) => ({
                  ...s,
                  targetIds: ids,
                  unitSymbol,
                }));
              }}
            >
              Tümünü seç
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setWastageBulkModal((s) => ({
                  ...s,
                  targetIds: [],
                  unitSymbol: "",
                }))
              }
            >
              Seçimi temizle
            </Button>
          </div>
          <Label>
            Fire miktarı
            {wastageBulkModal.unitSymbol
              ? ` (${wastageBulkModal.unitSymbol})`
              : ""}
          </Label>
          <div className="assign-qty-with-unit">
            <Input
              type="number"
              min="0"
              step="any"
              value={wastageBulkModal.draft}
              onChange={(v) => setWastageBulkModal((s) => ({ ...s, draft: v }))}
            />
            {wastageBulkModal.unitSymbol ? (
              <span className="assign-qty-with-unit__suffix">
                {wastageBulkModal.unitSymbol}
              </span>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              disabled={!wastageBulkModal.targetIds.length}
              onClick={() => {
                applyBulkWastageToOutputs(
                  wastageBulkModal.targetIds,
                  wastageBulkModal.draft,
                );
                addToast(
                  `${wastageBulkModal.targetIds.length} varyanta fire uygulandı`,
                  "success",
                );
                setWastageBulkModal({
                  open: false,
                  draft: "0",
                  targetIds: [],
                  unitSymbol: "",
                });
              }}
            >
              Uygula
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setWastageBulkModal({
                  open: false,
                  draft: "0",
                  targetIds: [],
                  unitSymbol: "",
                })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={wastageModal.open}
        onClose={() =>
          setWastageModal({
            open: false,
            outputPartId: null,
            draft: "0",
            unitSymbol: "",
          })
        }
        width="small"
      >
        <ModalHeader>Fire</ModalHeader>
        <ModalBody>
          <Label>
            Fire miktarı
            {wastageModal.unitSymbol ? ` (${wastageModal.unitSymbol})` : ""}
          </Label>
          <div className="assign-qty-with-unit">
            <Input
              type="number"
              min="0"
              step="any"
              value={wastageModal.draft}
              onChange={(v) => setWastageModal((s) => ({ ...s, draft: v }))}
            />
            {wastageModal.unitSymbol ? (
              <span className="assign-qty-with-unit__suffix">
                {wastageModal.unitSymbol}
              </span>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              onClick={() => {
                const pid = wastageModal.outputPartId;
                if (pid) {
                  updateVariantSettings(pid, { wastage: wastageModal.draft });
                }
                setWastageModal({
                  open: false,
                  outputPartId: null,
                  draft: "0",
                  unitSymbol: "",
                });
              }}
            >
              Kaydet
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setWastageModal({
                  open: false,
                  outputPartId: null,
                  draft: "0",
                  unitSymbol: "",
                })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {renderEntryEditModal()}

      <CardContainer className="assign-page-body">
        <div className="card border-0 assign-wizard-card">
          <ol className="assign-stepper mb-3" aria-label="Atama adımları">
            {ASSIGNMENT_STEPS.map((step) => {
              const done = step.id < activeStep;
              const active = step.id === activeStep;
              const reachable = step.id <= maxReachedStep;
              return (
                <li
                  key={step.key}
                  className={`assign-stepper__item${active ? " assign-stepper__item--active" : ""}${done ? " assign-stepper__item--done" : ""}`}
                >
                  <button
                    type="button"
                    className="assign-stepper__btn"
                    disabled={!reachable && !active}
                    onClick={() => goToStep(step.id)}
                  >
                    <span className="assign-stepper__top">
                      <span className="assign-stepper__num">{step.id}</span>
                      <span className="assign-stepper__label">
                        {step.label}
                      </span>
                    </span>
                  </button>
                  <span className="assign-stepper__connector" aria-hidden />
                </li>
              );
            })}
          </ol>

          {/* {activeStepMeta?.hint ? (
            <p className="assign-step-hint">{activeStepMeta.hint}</p>
          ) : null} */}

          {/* Part tab bar for steps 2–5 */}
          {renderPartTabs()}

          <div className="assign-wizard-card__body">
            {activeStep === 1 && (
              <div className="assign-section">
                <p className="assign-section__title d-flex align-items-center gap-2">
                  <FiServer size={18} />
                  Makine seçimi
                </p>
                <Select
                  className="mb-0"
                  label="Makine"
                  required
                  unique
                  options={machineOptions}
                  selected={formData.machine ? [String(formData.machine)] : []}
                  onSelected={(values) => {
                    handleMachineSelect(values?.[0]?.value || "");
                  }}
                  placeholder="— Makine seçin —"
                  disabled={!!editingMachineId}
                />
                {selectedMachine && (
                  <p className="text-muted small mt-3 mb-0">
                    Seçili:{" "}
                    <strong>
                      {selectedMachine.code} — {selectedMachine.name}
                    </strong>
                  </p>
                )}
              </div>
            )}
            {activeStep === 2 && (
              <>
                <p className="assign-section__title d-flex align-items-center gap-2 mb-3">
                  <FiList size={18} />
                  Girdiler
                </p>
                {renderEntriesStep()}
              </>
            )}
            {activeStep === 3 && (
              <>
                <p className="assign-section__title d-flex align-items-center gap-2 mb-3">
                  <FiPackage size={18} />
                  Çıktı
                </p>
                {renderOutputStep()}
              </>
            )}
            {activeStep === 4 && (
              <>
                <p className="assign-section__title d-flex align-items-center gap-2 mb-3">
                  <FiClock size={18} />
                  Varyant ayarları
                </p>
                {renderVariantSettingsStep()}
              </>
            )}
            {activeStep === 5 && (
              <>
                <p className="assign-section__title d-flex align-items-center gap-2 mb-3">
                  <FiCheck size={18} />
                  Özet
                </p>
                {renderSummaryStep()}
              </>
            )}
          </div>

          <div className="assign-footer">
            <Button
              appearance="danger-subtle"
              icon={<FiArrowLeft />}
              onClick={() =>
                activeStep === 1 ? navigate("/part-machines") : handlePrev()
              }
            >
              {activeStep === 1 ? "Listeye dön" : "Geri"}
            </Button>
            <div className="d-flex gap-2">
              {activeStep < ASSIGNMENT_STEPS.length ? (
                <Button
                  appearance="primary-animated"
                  icon={<FiChevronRightIcon />}
                  onClick={handleNext}
                >
                  İleri
                </Button>
              ) : (
                <Button
                  appearance="primary-animated"
                  onClick={handleSave}
                  disabled={saving || !formData.machine}
                  icon={<FiSave />}
                >
                  {saving ? "Kaydediliyor…" : "Kaydet"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContainer>
    </AppPage>
  );
};

export default withAuth(PartMachineAssignmentForm);
